import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  apiInvestigados,
  apiCentralNotarial,
  type BuscaDeEscrituras,
  type AtivoDoInvestigado,
  type BuscaNoDjen,
  type BuscaNoRab,
  type CoberturaDoCredito,
  type ConfirmacaoDeProcesso,
  type ProcessoAchado,
  type GrauDeCerteza,
  type PosicaoNoProcesso,
  type MotivoDeInteresse,
  type CategoriaDeAtivo,
  type ClassificacaoDoAtivo,
  type EfeitoPatrimonialDoAto,
  type MatriculaProposta,
  type ModalidadeDaAvaliacao,
  type PapelNoAtoNotarial,
  type EspecieDeVeiculo,
  type Investigado,
  type PesquisaGuardada,
  type AtoGuardado,
  type LeituraDoAto,
  type PlanoDeImoveis,
  type VarreduraDeImoveis,
} from '../lib/api';
import { BuscaDeAtivo } from './BuscaDeAtivo';
import { Confirmar } from './Confirmar';
import { usarEhMaster } from '../lib/papel';
import { paraTela } from '../lib/datas';
import { Valor } from './Valor';
import { avisar, avisarErro } from './Avisos';
import { completarReais, digitarReais } from '../lib/dinheiro.ts';
import { usarFormulario } from '../lib/formulario.ts';
import { GuardaDoFormulario } from './GuardaDoFormulario';
import { Icone } from './Icone';
import { AjudaDoPasso, BlocoDeLancar, CampoDoPasso, PassosDoTopo, Progresso, ResumoDoPasso } from './PassoAPasso';
import { TIPOS_DE_ATO } from '../lib/tipos-de-ato.ts';
import { EFEITOS_DO_ATO } from '../lib/efeito-do-ato.ts';
import { OpcoesAgrupadas } from './OpcoesAgrupadas';
import '../estilos/veiculos-atos-2026.css';
import '../estilos/linha-rapida-2026.css';

/*
 * ⭐ **ESTE ARQUIVO DEIXOU DE SER UMA TELA — 19/09/2026.** O componente
 * `AtivosLocalizados`, que desenhava os tópicos de bens, saiu quando o último
 * deles ganhou o desenho de Imóveis (PRD §46): Imóveis, Veículos, Outros ativos,
 * Atos notariais e Processos têm cada um o seu (`ImoveisDoCaso`,
 * `VeiculosDoCaso`, `OutrosDoCaso`, `AtosDoCaso`, `ProcessosDoCaso`). **Ficam
 * aqui as peças que eles usam** — as buscas, as fichas, os gravames, a
 * avaliação, a análise do processo —, as mesmas de antes. O comentário abaixo é
 * a história das regras que elas continuam seguindo.
 */

/**
 * A seção ATIVOS LOCALIZADOS — uma subseção por categoria de ativo.
 *
 * > *"Dentro dessa seção terão subseções dividindo pela categoria desse ativo."*
 * > — Richard, 24/08/2026
 *
 * ⚠️ **UM componente para as cinco subseções, filtrado por categoria.** Cinco
 * telas iguais divergiriam na primeira correção feita só numa delas — é a mesma
 * razão que fez a seção reusar o `Secao` da ficha do cliente em vez de uma
 * sanfona nova.
 *
 * ⚠️ **A régua de papel:** lançar é do colaborador — é o trabalho do dia, a
 * mesma que o deixa lançar passivo. **Corrigir e apagar são do master.**
 *
 * ⚠️ **A avaliação NÃO passa pelo sigilo de valores.** É o patrimônio do
 * investigado — o objeto do trabalho —, não o preço do serviço da KAIRÓS. Mesma
 * razão do valor da dívida e do passivo.
 */

/** As classificações que cada subseção oferece, na ordem da especificação. */
const CLASSIFICACOES: Record<CategoriaDeAtivo, ClassificacaoDoAtivo[]> = {
  imoveis: [
    'imovel_livre',
    'imovel_onerado',
    'imovel_transmitido',
    'imovel_matricula_encerrada',
    /* ⭐ A quinta, de 10/09/2026 — o recorte por titular. Vem por último porque
       é a única em que o investigado NUNCA foi dono. */
    'imovel_de_terceiro',
  ],
  /* ⚠️ `veiculo_situacao_nao_apurada` NÃO se oferece aqui: ela é o que a
     consulta produz quando a fonte não diz se o veículo ainda é dele, e quem
     lança à mão sabe qual é a situação — ou a deixa em branco de propósito. */
  veiculos: ['veiculo_atual', 'veiculo_historico', 'veiculo_baixado'],
  /* ⭐ **As duas espécies na mesma subseção desde 08/09/2026** — e quem lança
     escolhe qual é. É aqui que a distinção que ele pediu acontece. */
  atos: ['escritura', 'procuracao'],
  processos: ['processo_relevante'],
  /* ⭐ 18/09/2026. Quem lança escolhe na ficha O QUE é e, depois, a situação
     daquele bem — ver `SITUACOES_DO_OUTRO`. */
  outros: [
    'acao_em_nome',
    'acao_onerada',
    'acao_com_constricao',
    'acao_deixou_de_constar',
    'marca_em_vigor',
    'marca_pedido',
    'marca_com_onus',
    'marca_transferida',
    'marca_extinta',
    'patente_em_vigor',
    'patente_pedido',
    'patente_com_onus',
    'patente_transferida',
    'patente_extinta',
    'mineral_sem_onus_averbado',
    'mineral_onerado',
    'mineral_cedido',
    'mineral_extinto',
    'exterior_vigente',
    'exterior_transferida',
    'exterior_encerrada',
    'credito_livre',
    'credito_onerado',
    'credito_cedido',
    'credito_extinto',
  ],
};

export const ROTULO: Record<ClassificacaoDoAtivo, string> = {
  imovel_livre: 'Imóvel livre',
  /* ⭐ 16/10/2026 (PRD §51): as duas faces de "Livre, empresa do grupo". */
  imovel_livre_empresa_do_grupo: 'Imóvel livre, empresa do grupo',
  imovel_onerado: 'Imóvel onerado',
  imovel_transmitido: 'Imóvel transmitido',
  imovel_matricula_encerrada: 'Matrícula encerrada',
  imovel_de_terceiro: 'Imóvel em nome de terceiro',
  imovel_de_empresa_do_grupo: 'Imóvel em nome de empresa do grupo',
  veiculo_atual: 'Veículo — situação atual',
  veiculo_historico: 'Veículo — situação histórica',
  veiculo_baixado: 'Veículo baixado ou inativo',
  /* ⛔ **Estava sem rótulo e o selo saía em BRANCO na tela** — a lista de
     classificações do cliente não tinha este valor, e o `Record` exaustivo
     não cobra o que ele não sabe que existe. Ver a união em `lib/api.ts`. */
  veiculo_situacao_nao_apurada: 'Veículo — situação não apurada',
  escritura: 'Escritura pública',
  procuracao: 'Procuração pública',
  processo_relevante: 'Processo judicial relevante',
  acao_em_nome: 'Ação — em nome do investigado',
  acao_onerada: 'Ação — onerada',
  acao_com_constricao: 'Ação — com constrição judicial',
  acao_deixou_de_constar: 'Ação — deixou de constar',
  marca_em_vigor: 'Marca — em vigor',
  marca_pedido: 'Marca — pedido de registro',
  marca_com_onus: 'Marca — com ônus',
  marca_transferida: 'Marca — transferida',
  marca_extinta: 'Marca — extinta',
  patente_em_vigor: 'Patente — em vigor',
  patente_pedido: 'Patente — pedido',
  patente_com_onus: 'Patente — com ônus',
  patente_transferida: 'Patente — transferida',
  patente_extinta: 'Patente — extinta',
  mineral_sem_onus_averbado: 'Direito minerário — sem ônus averbado identificado',
  mineral_onerado: 'Direito minerário — onerado',
  mineral_cedido: 'Direito minerário — cedido',
  mineral_extinto: 'Direito minerário — extinto',
  /* ⭐ 07/10/2026: "Offshore" na tela, a pedido dele; no documento, "participação no exterior". */
  exterior_vigente: 'Offshore — participação confirmada',
  exterior_transferida: 'Offshore — participação transferida',
  exterior_encerrada: 'Offshore — sociedade encerrada',
  credito_livre: 'Direito creditório — livre',
  credito_onerado: 'Direito creditório — penhorado ou onerado',
  credito_cedido: 'Direito creditório — cedido',
  credito_extinto: 'Direito creditório — extinto ou recebido',
};

/**
 * O que cada classificação pede a mais.
 *
 * ⚠️ **Campo que a classificação não usa não aparece**, e não é economia de
 * tela: "matrícula sucessora" num imóvel livre é campo que convida a inventar
 * dado para preencher — o mesmo motivo pelo qual os cinco campos do card "Sobre
 * a dívida" dizem `(opcional)` por escrito.
 */
const PEDE_TRANSMISSAO: ClassificacaoDoAtivo[] = [
  'imovel_transmitido',
  'veiculo_historico',
  'escritura',
];
const PEDE_ENCERRAMENTO: ClassificacaoDoAtivo[] = ['imovel_matricula_encerrada'];
/**
 * ⭐⭐ **O que a quinta classificação pede — 10/09/2026.**
 *
 * ⛔ **O elemento de conexão NÃO é opcional aqui**, e é o único campo
 * obrigatório de todo este formulário. Listar patrimônio de quem não é parte no
 * caso, sem dizer o fato objetivo que justifica olhar, é acusação implícita
 * contra alguém que não consentiu. O servidor recusa sem ele — a garantia mora
 * lá, como a do valor que o colaborador não vê.
 */
const PEDE_CONEXAO: ClassificacaoDoAtivo[] = ['imovel_de_terceiro'];

/** ⭐ Escritura e procuração — 10/09/2026. O que todo ato notarial descreve. */
const PEDE_ATO_NOTARIAL: ClassificacaoDoAtivo[] = ['escritura', 'procuracao'];

const PAPEIS: Array<{ chave: PapelNoAtoNotarial; rotulo: string; deProcuracao: boolean }> = [
  { chave: 'comprador', rotulo: 'Comprador', deProcuracao: false },
  { chave: 'vendedor', rotulo: 'Vendedor', deProcuracao: false },
  { chave: 'doador', rotulo: 'Doador', deProcuracao: false },
  { chave: 'donatario', rotulo: 'Donatário', deProcuracao: false },
  { chave: 'cedente', rotulo: 'Cedente', deProcuracao: false },
  { chave: 'cessionario', rotulo: 'Cessionário', deProcuracao: false },
  { chave: 'permutante', rotulo: 'Permutante', deProcuracao: false },
  { chave: 'herdeiro', rotulo: 'Herdeiro', deProcuracao: false },
  { chave: 'meeiro', rotulo: 'Meeiro', deProcuracao: false },
  { chave: 'devedor', rotulo: 'Devedor', deProcuracao: false },
  { chave: 'credor', rotulo: 'Credor', deProcuracao: false },
  { chave: 'garantidor', rotulo: 'Garantidor', deProcuracao: false },
  { chave: 'outorgante', rotulo: 'Outorgante — quem dá os poderes', deProcuracao: true },
  { chave: 'procurador', rotulo: 'Procurador — quem recebe os poderes', deProcuracao: true },
  { chave: 'beneficiario', rotulo: 'Beneficiário', deProcuracao: false },
];


export const ROTULO_DO_PAPEL = Object.fromEntries(
  PAPEIS.map((x) => [x.chave, x.rotulo]),
) as Record<PapelNoAtoNotarial, string>;
/* ⭐ PRD §65 (27/10/2026): a lista dele mora em `lib/efeito-do-ato.ts`, espelho do servidor. */
export const ROTULO_DO_EFEITO = Object.fromEntries(
  EFEITOS_DO_ATO.map((x) => [x.chave, x.rotulo]),
) as Record<EfeitoPatrimonialDoAto, string>;
export const EFEITO_TIRA = Object.fromEntries(
  EFEITOS_DO_ATO.map((x) => [x.chave, x.bem === 'saiu']),
) as Record<EfeitoPatrimonialDoAto, boolean>;
/** ⭐ O ato pode ter tirado ou trazido o bem, conforme o papel do investigado (herança, partilha, sociedades, permuta). */
export const EFEITO_A_CONFERIR = Object.fromEntries(
  EFEITOS_DO_ATO.map((x) => [x.chave, x.bem === 'a_conferir']),
) as Record<EfeitoPatrimonialDoAto, boolean>;
const sufixoDoEfeito = (e: EfeitoPatrimonialDoAto) =>
  EFEITO_TIRA[e] ? ' (sai do patrimônio)' : EFEITO_A_CONFERIR[e] ? ' (depende do papel no ato)' : '';

/**
 * ⭐ **O que TODO imóvel descreve — 10/09/2026.** Tipo, onde fica e as áreas.
 * ⚠️ **Só imóveis**, e não é economia de tela: "área privativa" num veículo é
 * campo que convida a inventar dado para preencher.
 */
const PEDE_DESCRICAO_DO_IMOVEL: ClassificacaoDoAtivo[] = [
  'imovel_livre',
  'imovel_livre_empresa_do_grupo',
  'imovel_onerado',
  'imovel_transmitido',
  'imovel_matricula_encerrada',
  'imovel_de_terceiro',
  'imovel_de_empresa_do_grupo',
];

/** Área em m², no formato do Brasil. Nunca recebe nulo — quem chama filtra. */
/* ⭐ Ajustes da R4 (27/10/2026): com as quatro casas que a matrícula escreve — o painel da leitura arredondava
   "30,1234" para "30,12". */
export const emArea = (n: number) => n.toLocaleString('pt-BR', { maximumFractionDigits: 4 });

/** Uma linha da lista de avaliações no formulário — texto, como a pessoa digita. */
export type LinhaDeAvaliacao = {
  id?: string;
  modalidade: ModalidadeDaAvaliacao | '';
  valor: string;
  referencia: string;
  avaliadaEm: string;
  conta: boolean;
};

/** Como cada modalidade de avaliação se chama, e se ela é do próprio bem. */
export const MODALIDADES: Array<{ chave: ModalidadeDaAvaliacao; rotulo: string; documentada: boolean }> = [
  { chave: 'judicial', rotulo: 'Avaliação judicial', documentada: true },
  { chave: 'laudo', rotulo: 'Laudo de avaliação', documentada: true },
  { chave: 'declarada', rotulo: 'Valor declarado em documento', documentada: true },
  { chave: 'tabela_de_referencia', rotulo: 'Tabela pública de referência', documentada: false },
  { chave: 'comparativo_de_mercado', rotulo: 'Comparativo de mercado', documentada: false },
  { chave: 'preco_por_metro', rotulo: 'Preço médio por m²', documentada: false },
  /* ⭐ 15/09/2026 — as avaliações que ele listou para o imóvel. */
  { chave: 'valor_venal', rotulo: 'Valor venal', documentada: false },
  { chave: 'valor_da_terra_nua', rotulo: 'Valor da terra nua', documentada: false },
  { chave: 'avaliacao_mercadologica', rotulo: 'Avaliação mercadológica', documentada: false },
  { chave: 'a_apurar', rotulo: 'A apurar', documentada: false },
];

/* ⚠️ Derivados do array acima, para a linha do ativo não repetir a lista —
   duas listas da mesma coisa divergem na primeira correção feita numa só. */
/* ⭐ A cotação em bolsa (18/09/2026) fica FORA da lista de escolher — quem a
   escreve é o servidor, na ação — e entra aqui, para a linha do bem dizê-la. */
export const ROTULO_DA_MODALIDADE = {
  ...Object.fromEntries(MODALIDADES.map((m) => [m.chave, m.rotulo])),
  cotacao_em_bolsa: 'Cotação oficial em bolsa',
} as Record<ModalidadeDaAvaliacao, string>;
export const DOCUMENTADA = {
  ...Object.fromEntries(MODALIDADES.map((m) => [m.chave, m.documentada])),
  cotacao_em_bolsa: true,
} as Record<ModalidadeDaAvaliacao, boolean>;
export const PEDE_VALOR: ClassificacaoDoAtivo[] = [
  'imovel_livre',
  /* ⭐ 16/10/2026: o livre da empresa que é parte do caso soma, e pede valor.
     O de empresa que não é parte não pede, como o de terceiro. */
  'imovel_livre_empresa_do_grupo',
  'imovel_onerado',
  'imovel_transmitido',
  'imovel_matricula_encerrada',
  'veiculo_atual',
  'veiculo_historico',
  'veiculo_baixado',
];

/** O exemplo dentro de cada campo — campo sem exemplo é campo de dez jeitos. */
const PISTA: Record<CategoriaDeAtivo, string> = {
  imoveis: 'Apartamento — 2º Registro de Imóveis de Sorocaba/SP',
  veiculos: 'ABC1D23 — Fiat Toro Freedom 2019/2020',
  atos: 'Compra e venda — 5º Tabelião de Notas de São Paulo/SP',
  processos: 'Execução de título extrajudicial — 3ª Vara Cível de Sorocaba/SP',
  outros: 'Companhia Exemplo S.A. — ações ordinárias (ON)',
};

/**
 * Busca as aeronaves no RAB da ANAC — base local, **gratuita**.
 *
 * ⭐ **Esta grava sozinha**, ao contrário da de veículos: o RAB é um REGISTRO e
 * **declara a situação** — `DT_CANC` diz se a matrícula foi cancelada.
 * Classificar aqui é ler a fonte, não concluir.
 *
 * ⚠️ **A tela mostra as DUAS datas** — quando a ANAC atualizou e quando o Hub
 * importou. Base local que não diz sua idade é base que envelhece em silêncio.
 */
export function BuscarAeronaves({
  investigadoId,
  aoLancar,
}: {
  investigadoId: string;
  aoLancar: () => void;
}) {
  const [buscando, setBuscando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [r, setR] = useState<BuscaNoRab | null>(null);

  async function buscar() {
    setBuscando(true);
    setErro(null);
    try {
      const x = await apiInvestigados.aeronaves(investigadoId);
      setR(x.busca);
      if (x.busca.lancadas > 0) aoLancar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'A busca no RAB não foi.');
    } finally {
      setBuscando(false);
    }
  }

  const data = (d: string | null) =>
    d ? d.slice(0, 10).split('-').reverse().join('/') : '—';

  return (
    <BuscaDeAtivo
      rotulo="Buscar aeronaves"
      custo={{ tipo: 'gratuita' }}
      buscando={buscando}
      rotuloBuscando="Buscando no RAB…"
      erro={erro}
      aoBuscar={() => void buscar()}
      alcance={
        <>
          Registro Aeronáutico Brasileiro — base pública, <strong>já no servidor</strong>, sem
          custo. ⚠️ Cobre aeronave registrada no Brasil, e nada mais.
        </>
      }
    >
      {r?.falha && (
        <p className="erro">
          {r.falha} <strong>Isto não é "não tem aeronave"</strong> — é a base que não respondeu.
        </p>
      )}

      {r && !r.falha && (
        <div className="resultado-rab">
          <p className="ajuda">
            {r.encontradas} aeronave(s) no registro · {r.lancadas} lançada(s) aqui ·{' '}
            <strong>fonte de {data(r.fonteDe)}</strong>, importada em {data(r.importadoEm)}
          </p>
          {r.encontradas === 0 && (
            <p className="ajuda ressalvas-leitura">
              ⚠️ Nada no RAB neste documento. Isto <strong>não é</strong> "não tem aeronave" para
              quem tem CPF: o registro publica o CPF <strong>mascarado</strong>, e o casamento
              exige que o nome bata exatamente como está no registro.
            </p>
          )}
          {r.aeronaves.length > 0 && (
            <ul className="atos-lidos">
              {r.aeronaves.map((a) => (
                <li key={a.marcas}>
                  <span className="dado">{a.marcas}</span> · {a.fabricante} {a.modelo}
                  {a.anoFabricacao && <> · {a.anoFabricacao}</>}
                  {a.gravame && <> · <strong>{a.gravame}</strong></>}
                  {/*
                    ⚠️ **Como o casamento foi feito sai escrito.** Por CNPJ é
                    exato; por CPF depende do nome bater, e quem lê precisa saber
                    a diferença antes de levar isso a um documento assinado.
                  */}
                  <span className="ajuda">
                    {a.casadoPor === 'cnpj'
                      ? ' casado pelo CNPJ — exato'
                      : ' casado pelo CPF mascarado + nome'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </BuscaDeAtivo>
  );
}

/**
 * A BUSCA DE VEÍCULOS de um investigado.
 *
 * ⭐ **Saiu do LOTE em 08/09/2026**, na padronização que ele pediu. Antes era o
 * único botão da aba que vivia no topo, com caixas de marcar: um formato só
 * dele, que obrigava a entender uma mecânica diferente das outras buscas.
 *
 * ⚠️ **É a consulta mais cara da casa — R$ 15,41** —, e por isso o custo sai no
 * botão antes do clique e a confirmação é obrigatória.
 */
/**
 * ⭐⭐ **APROFUNDAR OS VEÍCULOS — 23/09/2026, pedido dele.**
 *
 * > *"Sem eles, 'veículo localizado' não é 'veículo penhorável'."* —
 * > `docs/FILA.md` §5, aberto desde agosto
 *
 * O histórico devolve **quais** veículos existem. O que decide se vale penhorar
 * vem daqui: **restrições** (bloqueio RENAJUD, judicial, tributária), **gravame**
 * (alienação fiduciária e o credor) e a **avaliação pelo chassi**, que resolve a
 * versão do modelo que a tabela gratuita não distingue.
 *
 * ⛔ **Um botão, não três.** As duas últimas dependem do protocolo da
 * consulta-base; separadas, o master pagaria a base três vezes.
 *
 * ⛔ **Enquanto o custo da consulta-base não estiver declarado, o botão não
 * aparece** — e a tela diz o que falta. Botão que gasta sem dizer quanto é o
 * que esta casa recusa ao colaborador, e a regra vale para o master também.
 */
export function AprofundarVeiculos({
  investigadoId,
  quantos,
  aoLancar,
}: {
  investigadoId: string;
  /** Quantos veículos há no caso — é o que multiplica o gasto. */
  quantos: number;
  aoLancar: () => void;
}) {
  const [indo, setIndo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [preco, setPreco] = useState<{
    porVeiculo: string | null;
    impedimento: string | null;
  } | null>(null);
  const [r, setR] = useState<{
    comGravame: number;
    comRestricao: number;
    comValor: number;
    semChassi: number;
    custo: string;
    avisos: string[];
  } | null>(null);

  useEffect(() => {
    void apiInvestigados
      .custoDoAprofundamento()
      .then((x) => setPreco({ porVeiculo: x.porVeiculo, impedimento: x.impedimento }))
      .catch(() => setPreco(null));
  }, []);

  async function aprofundar() {
    setIndo(true);
    setErro(null);
    try {
      const x = await apiInvestigados.aprofundarVeiculos(investigadoId);
      setR(x);
      aoLancar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'O aprofundamento não foi.');
    } finally {
      setIndo(false);
    }
  }

  if (quantos === 0) return null;
  /*
   * ⚠️ **Falta declarar algo: a tela DIZ o que falta, em vez de esconder.**
   * Botão que some sem explicação vira "o Hub não faz isso" na cabeça de quem
   * usa — e este faz, assim que o número existir.
   */
  if (preco?.impedimento) {
    return (
      <p className="ajuda-alerta ajuda-solta">
        ⚠️ Para aprofundar veículo — restrições, gravame e avaliação pelo chassi —{' '}
        {preco.impedimento}
      </p>
    );
  }

  return (
    <BuscaDeAtivo
      rotulo="Aprofundar os veículos"
      custo={{
        tipo: 'paga',
        quantas: quantos,
        unitario: preco?.porVeiculo !== null && preco?.porVeiculo !== undefined ? Number(preco.porVeiculo) : null,
      }}
      buscando={indo}
      rotuloBuscando="Aprofundando…"
      erro={erro}
      aoBuscar={() => void aprofundar()}
      alcance={
        <>
          Para cada veículo com chassi: <strong>restrições</strong> — bloqueio judicial
          (RENAJUD), judicial e tributária —, <strong>gravame</strong> com o credor, e a{' '}
          <strong>avaliação pelo chassi</strong>, que identifica a versão exata do modelo.
        </>
      }
      avisos={
        <>
          {/*
            ⛔ **Nada aqui apaga o que o caso já tem.** É a regra de 15/09/2026,
            e a tela a repete porque é o que decide o clique.
          */}
          <p>
            ⚠️ <strong>Nada é sobrescrito:</strong> avaliação que já existe fica como está,
            gravame já lançado não duplica, e a classificação do bem não muda.
          </p>
          <p>
            ⚠️ Veículo <strong>sem chassi gravado</strong> fica de fora — a consulta é por
            chassi, e sem ele não há o que perguntar.
          </p>
        </>
      }
    >
      {r && (
        <>
          <p className="ajuda">
            {r.comRestricao} com restrição · {r.comGravame} com gravame · {r.comValor} com
            avaliação nova{r.semChassi > 0 ? ` · ${r.semChassi} sem chassi` : ''} · gasto R${' '}
            {Number(r.custo).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
          </p>
          {/*
            ⚠️ **Falha de consulta é VISÍVEL** — e o que não é gravame aparece
            aqui para o master decidir onde entra. O Hub aponta; quem apura
            confirma.
          */}
          {r.avisos.map((a) => (
            <p key={a} className="ajuda-alerta ajuda-solta">{a}</p>
          ))}
        </>
      )}
    </BuscaDeAtivo>
  );
}

export function BuscarVeiculos({
  investigadoId,
  aoLancar,
}: {
  investigadoId: string;
  aoLancar: () => void;
}) {
  const [buscando, setBuscando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [r, setR] = useState<{ encontrados: number; ativos: number } | null>(null);

  async function buscar() {
    setBuscando(true);
    setErro(null);
    try {
      const x = await apiInvestigados.veiculos(investigadoId);
      setR({ encontrados: x.encontrados, ativos: x.ativos });
      if (x.ativos > 0) aoLancar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'A busca de veículos não foi.');
    } finally {
      setBuscando(false);
    }
  }

  return (
    <BuscaDeAtivo
      rotulo="Buscar veículos"
      custo={{ tipo: 'paga', quantas: 1, unitario: CUSTO_VEICULOS }}
      buscando={buscando}
      erro={erro}
      aoBuscar={() => void buscar()}
      alcance={
        <>
          Histórico de veículos — placa, modelo, ano, chassi e RENAVAM. A mesma consulta
          alimenta a ficha <strong>e</strong> esta seção.{' '}
          <strong>Não traz avaliação nem gravame:</strong> a FIPE custa R$ 5,00 à parte e o
          gravame R$ 7,39 por veículo, e sem eles “veículo localizado” não é “veículo
          penhorável”.
        </>
      }
      avisos={
        /*
          ⚠️ **A fonte não diz se o carro ainda é dele**, e o Hub não inventa:
          tudo entra como situação não apurada, fora da soma, esperando o master
          conferir e reclassificar.
        */
        <p>
          ⚠️ A consulta devolve o histórico e <strong>não declara a situação</strong>. Os
          veículos entram como <em>situação não apurada</em> e ficam fora do patrimônio atual
          até você reclassificar.
        </p>
      }
    >
      {r && (
        <p className="ajuda">
          {r.encontrados} veículo{r.encontrados === 1 ? '' : 's'} na fonte · {r.ativos} lançado
          {r.ativos === 1 ? '' : 's'} aqui
        </p>
      )}
    </BuscaDeAtivo>
  );
}

/**
 * O índice de COBERTURA, no topo de ATIVOS LOCALIZADOS.
 *
 * ⭐ **Substitui o índice de probabilidade de negociação** — a Assertiva
 * confirmou em 25/08/2026 que não o entrega por API, e a resposta não foi
 * comprar o mesmo escore de outro bureau: **é outra pergunta.**
 *
 * ⚠️ **Não devolve um número só, e a falta é deliberada.** Um índice único
 * juntaria o desembaraçado com o disputado e daria por alcançável o que está em
 * litígio. São linhas separadas, e quem as junta assume a junção.
 *
 * ⚠️ **E não classifica.** *"R$ 62.500 para R$ 850.000 de crédito"* é fato;
 * *"cobertura baixa"* é opinião — a mesma regra que proíbe o Hub de chamar um
 * passivo de "altíssimo risco".
 */
export function IndiceDeCobertura({
  casoId,
  chave,
  desenho,
}: {
  casoId: string;
  chave: string;
  /**
   * ⭐ **Sistema visual 2026, parte 3 — 14/09/2026.** Na folha, o índice ganha
   * título próprio e uma barra ao lado de cada número. ⚠️ **A barra não é
   * conta nova:** é o mesmo "cobre X%" desenhado, sempre na régua do crédito
   * do cliente — e sem crédito lançado não há régua, então não há barra.
   */
  desenho?: 'folha';
}) {
  const [c, setC] = useState<CoberturaDoCredito | null>(null);

  useEffect(() => {
    let vivo = true;
    void apiInvestigados
      .cobertura(casoId)
      .then((r) => vivo && setC(r.cobertura))
      .catch(() => vivo && setC(null));
    return () => {
      vivo = false;
    };
  }, [casoId, chave]);

  if (!c) return null;

  const pct = (v: number | null) => (v === null ? '—' : `${v.toFixed(1)}%`);

  const naFolha = desenho === 'folha';
  const barra = (v: number, tom: string) =>
    naFolha && c.credito !== null && c.credito > 0 ? (
      <span className={`cobertura-barra ${tom}`} aria-hidden="true">
        <i style={{ width: `${Math.min(100, Math.max(0, (v / c.credito) * 100))}%` }} />
      </span>
    ) : null;

  const indice = (
    <div className={'indice-cobertura' + (naFolha ? ' indice-na-folha' : '')}>
      {/*
        ⚠️ **A lacuna vem ANTES dos números**, e não depois: sem o crédito
        lançado os números abaixo não têm denominador, e mostrá-los primeiro
        faria parecer que há índice quando não há.
      */}
      {c.lacuna && <p className="ajuda ressalvas-leitura">⚠️ {c.lacuna}</p>}

      <div className="cobertura-linhas">
        <div>
          <span className="rotulo">Crédito do cliente</span>
          {c.credito !== null && barra(c.credito, 'barra-credito')}
          <strong className="dado">{c.credito === null ? '—' : dinheiro(c.credito)}</strong>
        </div>
        <div>
          <span className="rotulo">Patrimônio livre</span>
          {barra(c.livre, 'barra-livre')}
          <strong className="dado">{dinheiro(c.livre)}</strong>
          <span className="ajuda">cobre {pct(c.coberturaLivre)}</span>
        </div>
        <div>
          <span className="rotulo">Com o onerado</span>
          {barra(c.livre + c.onerado, 'barra-onerado')}
          <strong className="dado">{dinheiro(c.livre + c.onerado)}</strong>
          <span className="ajuda">cobre {pct(c.coberturaTotal)}</span>
        </div>
        {/*
          ⭐⭐ **AS AÇÕES COTADAS, NUMA LINHA PRÓPRIA — decisão dele, 18/09/2026.**
          ⛔ Não entram no livre nem no "com o onerado": o ônus de ação mora na
          B3, fora da vista de terceiros. Só aparece quando o caso tem ação —
          sem ela, um R$ 0,00 aqui seria ruído que parece achado.
        */}
        {c.acoesQuantas > 0 && (
          <div>
            <span className="rotulo">Ações cotadas em bolsa</span>
            {barra(c.acoes, 'barra-acoes')}
            <strong className="dado">{c.acoes > 0 ? dinheiro(c.acoes) : '—'}</strong>
            <span className="ajuda">{c.acoes > 0 ? `cobre ${pct(c.coberturaAcoes)} · linha própria` : 'sem quantidade e cotação'}</span>
          </div>
        )}
        <div>
          <span className="rotulo">Passivo do investigado</span>
          {barra(c.passivo, 'barra-passivo')}
          <strong className="dado">{dinheiro(c.passivo)}</strong>
          {/*
            ⚠️ **Não se subtrai do ativo, e a tela diz por quê.** Ordem de
            preferência entre credores é matéria jurídica, não aritmética —
            supor que o crédito do cliente entra na fila depois de todo o
            passivo seria concluir por quem tem OAB.
          */}
          <span className="ajuda">disputa o mesmo patrimônio</span>
        </div>
      </div>

      {/*
        ⛔ **A LINHA QUE FALTAVA — 08/09/2026.**
        Ele apontou: o cartão mostra `R$ 0,00` de patrimônio livre **sem
        explicar**, num caso em que há bem avaliado. O valor sempre esteve
        calculado; ninguém o dizia.

        ⚠️ **Vem ANTES da linha de "sem valor apurado", e são coisas
        diferentes:** aqui o valor EXISTE e a classificação o mantém fora; lá o
        valor não foi apurado. Misturar as duas faria a lacuna parecer regra e a
        regra parecer lacuna.

        ⚠️ **E o número NÃO se soma ao livre nem ao onerado.** Somar seria
        apresentar como alcançável o que a classificação diz que não é.
      */}
      {/*
        ⭐ **As ressalvas RECOLHEM — 08/09/2026.**

        > *"Toma a tela inteira para poucos campos."* — Richard

        ⛔ **Elas eram duas linhas longas em corpo minúsculo abaixo dos
        valores**, e é o que mais pesava na faixa. ⚠️ **Não somem:** explicam
        por que um número é o que é, e sem elas um R$ 0,00 vira "não tem nada".
        Ficam atrás de um resumo que diz **quantas** são — quem vê "2 ressalvas"
        sabe que há o que ler.

        ⚠️ **O resumo conta, e não convida:** "ver detalhes" não diz se vale
        abrir; "2 ressalvas sobre o que ficou fora" diz.
      */}
      {(c.foraQuantos > 0 || c.foraPorRegraQuantos > 0 || c.ativosSemValor > 0 || c.passivosSemValor > 0) && (
        <details className="cobertura-ressalvas">
          <summary>
            {(() => {
              const n = [c.foraQuantos > 0, c.foraPorRegraQuantos > 0, c.ativosSemValor > 0 || c.passivosSemValor > 0].filter(Boolean).length;
              return `${n} ressalva${n > 1 ? 's' : ''} sobre o que ficou fora da conta`;
            })()}
          </summary>

          {/* ⛔ A lista desta frase era escrita à mão, e ficou para trás duas
              vezes (a situação não apurada, e em 18/09 os outros ativos). Agora
              ela diz o critério, que não envelhece; o que cada bem é, a lista
              de cada tópico diz. E só fala em valor quando há valor. */}
          {c.foraQuantos > 0 && (
            <p className="ajuda">
              ⚠️ Fora do patrimônio atual pela situação:{' '}
              <strong>
                {c.foraDaSoma > 0 ? `${dinheiro(c.foraDaSoma)} em ` : ''}
                {c.foraQuantos} bem(ns)
              </strong>{' '}
              — o que saiu do patrimônio, o de situação não apurada, escrituras, procurações e
              processos. <strong>Isso não é lacuna</strong>: é a situação de cada um que o
              mantém fora.
            </p>
          )}

          {/* ⭐ 18/09/2026: marca, patente e mineração — são da parte, e a regra dele os deixa fora. */}
          {c.foraPorRegraQuantos > 0 && (
            <p className="ajuda">
              ⚠️ Fora da conta por regra: <strong>{c.foraPorRegraQuantos} bem(ns)</strong> — marca,
              patente ou direito minerário. São da parte; ficam fora porque não têm cotação: o valor
              depende de laudo e a venda, de comprador habilitado.
            </p>
          )}

          {(c.ativosSemValor > 0 || c.passivosSemValor > 0) && (
            <p className="ajuda">
              ⚠️ Fora da conta por falta de valor apurado: {c.ativosSemValor} ativo(s) ·{' '}
              {c.passivosSemValor} passivo(s). <strong>Valor em branco não é zero</strong> — a
              cobertura real pode ser maior.
            </p>
          )}
        </details>
      )}
    </div>
  );

  if (!naFolha) return indice;

  return (
    <section className="s-folha rel-cobertura" aria-labelledby="rel-cobertura-titulo">
      <div className="s-folha-topo">
        <h2 id="rel-cobertura-titulo" className="s-folha-titulo">
          Índice de cobertura
        </h2>
        <p className="subtitulo">O que se localizou, diante do crédito do cliente.</p>
      </div>
      <div className="s-folha-corpo">{indice}</div>
    </section>
  );
}

/**
 * As três espécies de bem móvel — decisão do Richard, 25/08/2026.
 *
 * ⚠️ **Espécie e situação são eixos diferentes.** Um automóvel histórico e uma
 * aeronave atual continuam cada um com a sua classificação; a espécie só diz
 * **que bem é**, e agrupa a lista em três blocos.
 */
const ESPECIE: Record<EspecieDeVeiculo, string> = {
  automovel: 'Automóveis',
  aeronave: 'Aeronaves',
  embarcacao: 'Embarcações',
};

const ESPECIES: EspecieDeVeiculo[] = ['automovel', 'aeronave', 'embarcacao'];

/**
 * De onde vem cada espécie — dito na tela, porque muda o que se pode esperar.
 *
 * ⚠️ **Botão que não busca não existe**, e aqui a diferença é gritante: o
 * automóvel tem consulta paga, a aeronave tem base pública gratuita e a
 * embarcação não tem caminho nenhum. Calar isso faria "nada lançado" parecer
 * "não tem" nos três casos.
 */
export const FONTE_DA_ESPECIE: Record<EspecieDeVeiculo, string> = {
  automovel: 'Busca automática pela Assertiva, por CPF ou CNPJ — R$ 15,41.',
  aeronave:
    'Registro Aeronáutico Brasileiro da ANAC — base pública, gratuita e atualizada ' +
    'diariamente. ⚠️ CNPJ vem completo; CPF vem parcialmente mascarado.',
  embarcacao:
    '⛔ Sem busca automática: o Tribunal Marítimo registra só acima de 100 toneladas ' +
    'brutas, a Capitania inscreve o resto sem busca central, e a ANTAQ cobre apenas ' +
    'empresa de navegação. Lançamento à mão.',
};

/**
 * O dinheiro do INVESTIGADO — avaliação de bem, passivo, crédito do cliente.
 *
 * ## ⚠️ Por que ele NÃO passa pelo componente `Valor` — escrito em 08/09/2026
 *
 * A regra da casa é *"todo dinheiro na tela passa pelo `Valor`"*, e olhar este
 * arquivo de fora dá a impressão de violação: `Valor` aparece **7 vezes** e
 * `dinheiro()` **12**. **Não é descuido, e a divisão é exata:**
 *
 * | Quem usa | O que é |
 * |---|---|
 * | `Valor` | **dinheiro da KAIRÓS** — o custo das cópias de ato, que sai do Fundo de Custas |
 * | `dinheiro()` | **dinheiro do INVESTIGADO** — avaliação, passivo, crédito do cliente |
 *
 * ⭐ **O olho do cabeçalho esconde o que é da KAIRÓS**, e é para isso que ele
 * existe: honorário e custo não se mostram numa reunião. O patrimônio do
 * investigado é **o objeto do trabalho** — escondê-lo tornaria a tela inútil na
 * própria reunião em que ela é usada. É a mesma decisão já escrita em
 * `MarcosDaDivida.tsx` sobre o valor da dívida: *"não é dinheiro da KAIRÓS, por
 * isso aparece para todo mundo"*.
 *
 * ⛔ **Não troque `dinheiro()` por `Valor` aqui achando que conserta a regra.**
 * Trocar apagaria da tela o patrimônio que o Richard precisa ler.
 */
export const dinheiro = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/**
 * ⚠️ **Só VEÍCULOS tem busca automática hoje, e não é escolha: é o que existe.**
 *
 * A pesquisa em 24/08/2026 fechou os três caminhos de imóvel por CPF — a
 * "Pesquisa Prévia de Bens" da Infosimples está descontinuada (e cobria só SP,
 * exigindo o certificado da KAIRÓS), o ONR publica API só para cartórios, e o
 * Assertiva 360 traz "Imóveis PF e PJ" a custo zero **sem API**. Escritura e
 * procuração vivem no CENSEC, que pede certificado e tem captcha.
 *
 * ⚠️ **Botão que não busca nada é pior que botão nenhum:** quem clica e não vê
 * resultado conclui que não há bem, quando o que não houve foi consulta. Onde
 * não há fonte, a tela **diz o que falta** em vez de oferecer um botão morto.
 */
const CUSTO_VEICULOS = 15.41;

/**
 * ⭐⭐ **O FORMULÁRIO DE GRAVAME — 10/09/2026.**
 *
 * ⚠️ **Uma linha por gravame, e "repetir a linha acima".** As quatro
 * indisponibilidades da matrícula real diferem entre si **só pelo processo e
 * pela data** — natureza e órgão são os mesmos. Sem o repetir, são dezesseis
 * campos digitados para lançar quatro fatos.
 *
 * ⚠️ **A situação nasce em "não apurada", e é decisão.** Ele está lendo um
 * escaneado ruim: obrigar a escolher entre ativo e cancelado quando a averbação
 * de baixa está ilegível é obrigar a inventar dado num campo que decide
 * dinheiro. ⛔ **Só "ativo" soma.**
 */
export function FormularioDeOnus({
  ativo,
  investigadoId,
  aoLancar,
  comProcesso = false,
}: {
  ativo: AtivoDoInvestigado;
  investigadoId: string;
  aoLancar: () => void;
  /** ⭐ 27/10/2026 (PRD §65): o campo do processo, por ora só nos imóveis (pedido dele). */
  comProcesso?: boolean;
}) {
  const [aberto, setAberto] = useState(false);
  const [natureza, setNatureza] = useState('');
  const [credor, setCredor] = useState('');
  const [processo, setProcesso] = useState('');
  const [valor, setValor] = useState('');
  const [data, setData] = useState('');
  const [situacao, setSituacao] = useState('');
  const [gravando, setGravando] = useState(false);

  /** Vazio vira nulo, e nunca zero — "não quantificado na matrícula". */
  const numero = (t: string) => (t.trim() === '' ? null : Number(t.replace(/\./g, '').replace(',', '.')));

  async function lancar(e: React.FormEvent) {
    e.preventDefault();
    if (natureza.trim().length < 2) return;
    setGravando(true);
    try {
      await apiInvestigados.acrescentarOnus(investigadoId, ativo.id, {
        natureza,
        credor: credor.trim() || null,
        ...(comProcesso ? { processo: processo.trim() || null } : {}),
        valor: numero(valor),
        constituidoEm: data || null,
        situacao: situacao || null,
      });
      /*
       * ⭐ **Só o que muda entre gravames é limpo.** Natureza, órgão e situação
       * ficam — é o "repetir a linha acima" sem botão nenhum: lançar a segunda
       * indisponibilidade do mesmo juízo custa dois campos, não cinco.
       */
      setValor('');
      setData('');
      aoLancar();
      avisar('Gravame lançado.');
    } catch (err) {
      avisarErro(err);
    } finally {
      setGravando(false);
    }
  }

  /* ⭐ No desenho novo desde 18/09/2026 (o bloco de lançar): cada campo diz o
     que é. Serve à linha do bem e ao passo "Gravames" das fichas de imóvel e de
     veículo — um formulário só. */
  if (!aberto) {
    return (
      <button type="button" className="imv-mais" onClick={() => setAberto(true)}>
        <Icone nome="mais" tamanho={14} /> Acrescentar gravame
      </button>
    );
  }

  return (
    <BlocoDeLancar
      titulo="Acrescentar gravame"
      aoEnviar={(e) => void lancar(e)}
      nota={
        <p>
          Só <strong>&ldquo;ativo&rdquo;</strong> soma. Valor em branco é <strong>não quantificado na matrícula</strong>, nunca zero. ⚠️ Na penhora, o
          que a averbação nomeia costuma ser o <strong>juízo</strong>, não o credor.
        </p>
      }
      acoes={
        <>
          <button type="button" className="imv-bt fantasma" onClick={() => setAberto(false)}>
            Fechar
          </button>
          <button type="submit" className="imv-bt primario" disabled={gravando || !natureza.trim()}>
            <Icone nome="mais" tamanho={14} /> {gravando ? 'Gravando…' : 'Lançar gravame'}
          </button>
        </>
      }
    >
      <CampoDoPasso rotulo="Natureza" obrigatorio col={3}>
        <input list="naturezas-de-onus" value={natureza} onChange={(e) => setNatureza(e.target.value)} placeholder="Alienação fiduciária" />
      </CampoDoPasso>
      {/* ⚠️ Lista ABERTA, com sugestões: a matrícula traz gravame que nenhuma
          lista fecha, e travar em doze deixaria o resto sem onde cair. */}
      <datalist id="naturezas-de-onus">
        <option value="Alienação fiduciária" />
        <option value="Hipoteca" />
        <option value="Penhora" />
        <option value="Arresto" />
        <option value="Sequestro" />
        <option value="Indisponibilidade" />
        <option value="Usufruto" />
        <option value="Promessa registrada" />
        <option value="Cessão fiduciária" />
        <option value="Averbação premonitória" />
        <option value="Citação de ação real" />
      </datalist>
      <CampoDoPasso rotulo="Credor ou órgão" col={3}>
        <input value={credor} onChange={(e) => setCredor(e.target.value)} placeholder="Banco do Brasil · TRT 2ª Região" />
      </CampoDoPasso>
      {comProcesso && (
        <CampoDoPasso rotulo="Processo" col={3} mono>
          <input value={processo} onChange={(e) => setProcesso(e.target.value)} placeholder="0000000-00.0000.0.00.0000" />
        </CampoDoPasso>
      )}
      <CampoDoPasso rotulo="Valor" col={2} mono>
        <i className="imv-pre">R$</i>
        <input inputMode="decimal" value={valor} onChange={(e) => setValor(digitarReais(e.target.value))} onBlur={(e) => setValor(completarReais(e.target.value))} placeholder="267.100,00" />
      </CampoDoPasso>
      <CampoDoPasso rotulo="Constituído em" col={2} mono>
        <input type="date" value={data} onChange={(e) => setData(e.target.value)} />
      </CampoDoPasso>
      <CampoDoPasso rotulo="Situação" col={2}>
        <select value={situacao} onChange={(e) => setSituacao(e.target.value)}>
          <option value="">não apurada</option>
          <option value="ATIVO">ativo</option>
          <option value="CANCELADO">cancelado (baixa averbada)</option>
          <option value="QUITADO SEM BAIXA">quitado sem baixa averbada</option>
        </select>
      </CampoDoPasso>
    </BlocoDeLancar>
  );
}

/**
 * ⭐⭐ **A NOSSA ANÁLISE DO PROCESSO — 14/09/2026, pedido dele.**
 *
 * > *"No campo Processos Judiciais não precisamos dessa tela de gravame,
 * > podemos substituir por uma caixa de texto, pra incluir a nossa análise do
 * > processo."* — Richard
 *
 * ⭐ **Sai no relatório, abaixo do processo** (decisão dele). Grava ao sair do
 * campo, como o texto das seções, e o servidor devolve o que a conferência
 * achou — avisa e não bloqueia.
 */
export function AnaliseDoProcesso({
  ativo,
  investigadoId,
  aoMudar,
  rotulo = 'Nossa análise do processo',
  placeholder = 'O que este processo significa para o caso: fase, o que já se pediu, o que ele revela.',
  nota = 'Sai no relatório, abaixo deste processo. Grava ao sair do campo.',
  linhas = 4,
}: {
  ativo: AtivoDoInvestigado;
  investigadoId: string;
  aoMudar: () => void;
  /** ⭐ 28/10/2026 (PRD §72): a mesma caixa serve à "Obs." do imóvel, com outro rótulo e outra nota. */
  rotulo?: string;
  placeholder?: string;
  nota?: string;
  linhas?: number;
}) {
  const [texto, setTexto] = useState(ativo.analise ?? '');
  const [estado, setEstado] = useState<'parado' | 'gravando' | 'gravado'>('parado');
  const [achados, setAchados] = useState<
    Array<{ gravidade: 'grave' | 'atencao'; aviso: string; trecho: string }>
  >([]);

  useEffect(() => {
    setTexto(ativo.analise ?? '');
  }, [ativo.analise]);

  async function gravar() {
    if (texto.trim() === (ativo.analise ?? '').trim()) return;
    setEstado('gravando');
    try {
      const r = await apiInvestigados.salvarAnaliseDoProcesso(investigadoId, ativo.id, texto);
      setAchados(r.achados);
      setEstado('gravado');
      aoMudar();
    } catch (e) {
      setEstado('parado');
      avisarErro(e);
    }
  }

  const id = `analise-${ativo.id}`;
  return (
    <div className="imv-campo analise-do-processo">
      <label htmlFor={id} className="imv-rotulo">
        {rotulo}
        {estado === 'gravando' && <span className="lr-estado"> · gravando…</span>}
        {estado === 'gravado' && <span className="lr-estado"> · gravado</span>}
      </label>
      <span className="imv-in lr-texto">
        <textarea
          id={id}
          rows={linhas}
          value={texto}
          placeholder={placeholder}
          onChange={(e) => {
            setTexto(e.target.value);
            if (estado === 'gravado') setEstado('parado');
          }}
          onBlur={() => void gravar()}
        />
      </span>
      <small>{nota}</small>
      {achados.length > 0 && (
        <div className="aviso-atencao">
          <p className="rotulo">A conferência achou isto no texto</p>
          <ul className="atos-lidos">
            {achados.map((a, i) => (
              <li key={`${i}-${a.aviso}`}>
                <strong>{a.aviso}</strong>
                <br />
                <span className="ajuda">{a.trecho}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * A BUSCA DE IMÓVEIS de um investigado — ele **e as empresas dele**.
 *
 * > *"Resolver a questão de pesquisa de imóveis para CPF e CNPJ (botão
 * > individual em cada investigado)."* — Richard, 08/09/2026
 *
 * ⭐ **Consulta o documento da pessoa E os CNPJs das participações
 * societárias.** Até aqui só o documento dela era olhado, e o patrimônio que
 * está no nome das empresas **não era procurado** — havia onze participações
 * gravadas no caso real, nenhuma consultada.
 *
 * ⚠️ **O plano é carregado ANTES e não gasta nada:** é ele que permite ao botão
 * dizer quantos documentos serão consultados antes do clique.
 *
 * ⚠️ **Não grava lançamento.** A base diz que o imóvel existe e quem responde
 * por ele; **não diz se há ônus na matrícula**. O master lança depois de olhar.
 */
export function BuscarImoveis({
  investigadoId,
  chave,
}: {
  investigadoId: string;
  chave: string;
}) {
  const [plano, setPlano] = useState<PlanoDeImoveis | null>(null);
  const [preco, setPreco] = useState<number | null>(null);
  /* ⭐ **De onde o preço veio** — medido no histórico ou tabelado no contrato.
     A tela escreve "cerca de" no primeiro caso (22/09/2026). */
  const [precoAproximado, setPrecoAproximado] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  /* O resumo da busca que acabou de rodar (quantos, quanto custou). A LISTA não
     mora mais aqui: vem do banco, no plano (`ultima` de cada documento). */
  const [busca, setBusca] = useState<VarreduraDeImoveis | null>(null);

  async function carregarPlano() {
    try {
      const r = await apiInvestigados.planoDeImoveis(investigadoId);
      setPlano(r.plano);
      setPreco(r.precoDeTabela);
      setPrecoAproximado(r.precoAproximado === true);
    } catch {
      setPlano(null);
    }
  }

  useEffect(() => {
    void carregarPlano();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investigadoId, chave]);

  async function buscar(modo: 'o-que-falta' | 'tudo') {
    setBuscando(true);
    setErro(null);
    setBusca(null);
    try {
      const r = await apiInvestigados.buscarImoveis(investigadoId, modo);
      setBusca(r.busca);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'A busca de imóveis não foi.');
    } finally {
      /* ⚠️ **Recarrega MESMO quando dá erro** — 14/09/2026. Com a conexão
         esgotada, o servidor segue consultando e guardando; sem recarregar, a
         tela esconderia o que já foi pago. */
      await carregarPlano();
      setBuscando(false);
    }
  }

  if (plano === null) return null;

  /*
   * ⭐⭐ **O QUE FALTA, e não tudo de novo — 14/09/2026.**
   *
   * > *"Sempre salvar as pesquisas que forem sendo realizadas… tendo assim que
   * > pagar novamente para pesquisar novamente."* — Richard
   *
   * Documento que já respondeu tem a resposta guardada e não entra no clique.
   * Falha conta como "falta" — a fonte não respondeu, e perguntar de novo é o
   * que se quer. **Consultar de novo o que respondeu** só aparece quando não
   * falta nada, e avisa que custa de novo.
   */
  const pendentes = plano.alvos.filter((a) => !a.ultima || a.ultima.falha !== null);
  const respondidos = plano.alvos.filter((a) => a.ultima && a.ultima.falha === null);
  const modo: 'o-que-falta' | 'tudo' = pendentes.length > 0 ? 'o-que-falta' : 'tudo';
  const quantas = modo === 'tudo' ? plano.alvos.length : pendentes.length;
  const rotulo =
    modo === 'tudo'
      ? 'Buscar tudo de novo'
      : respondidos.length > 0
        ? 'Buscar o que falta'
        : 'Buscar imóveis';
  const quando = (iso: string) =>
    new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Sao_Paulo',
    });
  const guardadas = plano.alvos.filter((a) => a.ultima);
  const comImovel = respondidos.filter((a) =>
    a.ultima!.imoveis ? a.ultima!.imoveis.length > 0 : a.ultima!.itens > 0,
  ).length;

  return (
    <BuscaDeAtivo
      rotulo={rotulo}
      custo={{ tipo: 'paga', quantas, unitario: preco, ...(precoAproximado ? { aproximado: true as const } : {}) }}
      buscando={buscando}
      erro={erro}
      aoBuscar={() => void buscar(modo)}
      alcance={
        <>
          Imóveis da <strong>União</strong> — terrenos de marinha, aforamento e ocupação — no
          documento desta parte{' '}
          {plano.dasEmpresas > 0 && (
            <>
              e nos <strong>{plano.dasEmpresas} CNPJ{plano.dasEmpresas === 1 ? '' : 's'} das
              empresas</strong> em que ela é sócia
            </>
          )}
          . ⚠️ <strong>É um recorte, não o patrimônio imobiliário:</strong> não existe consulta
          automática que devolva imóvel urbano por CPF. Esse caminho é a pesquisa no portal de
          registro de imóveis, e a matrícula encontrada se sobe logo abaixo.
        </>
      }
      oQueSeraOlhado={
        plano.alvos.length > 0 && (
          <ul className="atos-lidos">
            {(modo === 'tudo' ? plano.alvos : pendentes).map((a) => (
              <li key={a.documento}>
                <strong>{a.empresa ?? a.investigado}</strong> ·{' '}
                <span className="dado">{a.documentoFormatado}</span>
                {a.empresa && (
                  <span className="ajuda">
                    {' '}
                    empresa desta parte
                    {a.tambemDe.length > 0 && <> · também de {a.tambemDe.join(', ')}</>}
                  </span>
                )}
                {a.ultima?.falha && (
                  <span className="ajuda"> · da última vez falhou</span>
                )}
              </li>
            ))}
          </ul>
        )
      }
      avisos={
        <>
          {modo === 'tudo' && (
            <p>
              ⚠️ <strong>Todos os documentos já responderam</strong>, e a resposta está guardada
              abaixo. Consultar de novo <strong>custa de novo</strong> — faz sentido quando a
              resposta pode ter mudado.
            </p>
          )}
          {modo === 'o-que-falta' && respondidos.length > 0 && (
            <p>
              ⭐ <strong>
                {respondidos.length === 1
                  ? '1 documento já respondeu'
                  : `${respondidos.length} documentos já responderam`}
              </strong>{' '}
              e não {respondidos.length === 1 ? 'será consultado' : 'serão consultados'} de novo — a
              resposta está guardada.
            </p>
          )}
        </>
      }
    >
      {/*
        ⚠️ **Sem documento, a busca não existe — e a tela diz na linha de quem
        falta.** Não é erro de cadastro que se cale: é o que trava a automação.
      */}
      {plano.semDocumento.length > 0 && (
        <p className="ajuda">
          ⚠️ <strong>Sem CPF ou CNPJ cadastrado:</strong> {plano.semDocumento.join(' · ')}. Sem
          documento não há de onde consultar.
        </p>
      )}

      {/*
        ⭐⭐ **A RESPOSTA GUARDADA de cada documento — 14/09/2026.** Vem do banco:
        reabrir o caso mostra o que já se pagou para saber, com a data.
      */}
      {guardadas.length > 0 && (
        <div className="leitura-matricula">
          <p className="ajuda">
            <strong>Resposta guardada de cada documento</strong> ·{' '}
            {comImovel} com imóvel localizado
            {busca && (
              <>
                {' '}
                · esta busca: {busca.consultados} consultado{busca.consultados === 1 ? '' : 's'}, custo{' '}
                <Valor de={busca.custoTotal} />
              </>
            )}
          </p>
          <ul className="atos-lidos">
            {plano.alvos.map((a) => (
              <li key={a.documento}>
                <strong>{a.empresa ?? a.investigado}</strong> ·{' '}
                <span className="dado">{a.documentoFormatado}</span>
                {/*
                  ⭐ **A ETIQUETA — decisão dele de 08/09/2026.** O imóvel achado
                  no CNPJ de uma empresa aparece na lista da pessoa, dizendo que
                  o bem está no nome da empresa. Sem isto, quem lê não saberia
                  que a titularidade é de outra pessoa jurídica — que é o que
                  decide a estratégia de execução.
                */}
                {a.empresa && (
                  <p className="ajuda">
                    bem em nome da empresa, não da pessoa
                    {a.tambemDe.length > 0 && <> · também sócia: {a.tambemDe.join(', ')}</>}
                  </p>
                )}
                {!a.ultima ? (
                  <p className="ajuda">Ainda não consultado.</p>
                ) : a.ultima.falha ? (
                  <p className="erro">
                    Em {quando(a.ultima.em)} a consulta falhou: {a.ultima.falha}.{' '}
                    <strong>Isto não é “não tem imóvel”</strong> — é a fonte dizendo que não
                    conseguiu responder.
                  </p>
                ) : a.ultima.imoveis === null ? (
                  <p className="ajuda">
                    Respondeu em {quando(a.ultima.em)} com {a.ultima.itens} imóve
                    {a.ultima.itens === 1 ? 'l' : 'is'}, mas a lista é de antes de 14/09/2026, quando
                    ainda não se guardava. Consulte de novo para ver.
                  </p>
                ) : a.ultima.imoveis.length === 0 ? (
                  <p className="ajuda">
                    Respondeu em {quando(a.ultima.em)}: não há imóvel da União neste documento.
                  </p>
                ) : (
                  <>
                    <p className="ajuda">Respondeu em {quando(a.ultima.em)}:</p>
                    <ul className="atos-lidos">
                      {a.ultima.imoveis.map((i) => (
                        <li key={i.rip ?? i.endereco}>
                          {i.rip && (
                            <>
                              <span className="dado">RIP {i.rip}</span> ·{' '}
                            </>
                          )}
                          {i.endereco ?? 'endereço não informado'}
                          {i.natureza && <> · {i.natureza}</>}
                          {i.conceituacao && <> · {i.conceituacao}</>}
                          {i.areaTotal !== null && (
                            <>
                              {' '}
                              · <span className="dado">{i.areaTotal} m²</span>
                            </>
                          )}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </li>
            ))}
          </ul>
          <p className="ajuda">
            ⚠️ <strong>Nada foi lançado.</strong> A base diz que o imóvel existe e quem responde
            por ele, <strong>não diz se há ônus na matrícula</strong>. Lance cada imóvel
            escolhendo a classificação depois de olhar a matrícula.
          </p>
        </div>
      )}
    </BuscaDeAtivo>
  );
}

const SELO_DO_GRAU: Record<GrauDeCerteza, string> = {
  confirmado: 'Confirmado',
  provavel: 'Provável',
  homonimo: 'Pode ser homônimo',
};

/**
 * ⛔ **O que a tela escreve sobre a posição — e ela mentia até 29/08/2026.**
 * A pergunta era só *"é autor?"*, e tudo que não fosse virava "polo passivo",
 * inclusive terceiro interessado. `Record` exaustivo: posição nova sem linha
 * aqui **não compila**, em vez de sair como réu sem ninguém notar.
 */
const DIZER_A_POSICAO: Record<PosicaoNoProcesso, string> = {
  autor: 'ele é quem cobra',
  reu: 'ele é quem é cobrado',
  terceiro: 'terceiro interessado',
  nao_declarado: 'posição não declarada',
};

/**
 * O motivo de o processo estar no primeiro bloco, escrito em cada linha.
 *
 * ⚠️ **Escrever o motivo não é enfeite: é o que torna honesta a escolha do
 * Richard** de incluir as execuções contra ele. O bloco de cima mistura o que
 * ele **tem** com o que ele **deve**, e a mistura só é aceitável enquanto cada
 * linha diz qual das duas coisas é. É a mesma razão de os quatro dinheiros do
 * `CLAUDE.md` nunca se somarem.
 */
const DIZER_O_MOTIVO: Record<MotivoDeInteresse, string> = {
  ele_cobra: 'Ele é quem cobra',
  desconsideracao: 'Desconsideração da personalidade jurídica',
  sucessao: 'Herança',
  insolvencia: 'Recuperação judicial ou falência',
  execucao_contra_ele: 'Execução contra ele',
  cumprimento_contra_ele: 'Cumprimento de sentença contra ele',
};

/**
 * ⚠️ **A ordem dentro do primeiro bloco, e por que a certeza vem ANTES do
 * motivo.** Ordenar por motivo primeiro poria uma desconsideração de um
 * possível homônimo acima de um processo confirmado — e "pode ser outra pessoa"
 * é a informação mais forte que existe aqui: sem identidade, o achado não vale.
 * Dentro de cada grau de certeza, o mais raro sobe: **três desconsiderações e
 * uma herança se perderiam entre dezoito "ele cobra"**.
 */
const PESO_DO_MOTIVO: Record<MotivoDeInteresse, number> = {
  insolvencia: 0,
  sucessao: 1,
  desconsideracao: 2,
  ele_cobra: 3,
  execucao_contra_ele: 4,
  cumprimento_contra_ele: 5,
};

/** Data e hora de São Paulo, para "guardada de…" — 14/09/2026. */
function quandoFoi(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Sao_Paulo',
  });
}

function maisRaro(p: ProcessoAchado): number {
  return Math.min(...p.interesse.map((m) => PESO_DO_MOTIVO[m]), 99);
}

/**
 * Busca os processos no diário do CNJ, pelo nome, e mostra **o quanto o Hub tem
 * certeza de que é a pessoa certa**.
 *
 * ⚠️ **O diário não devolve CPF** — o parâmetro existe e é ignorado em silêncio.
 * Então a busca traz os homônimos junto, e a tela precisa dizer isso com todas as
 * letras: processo de estranho apresentado como do investigado é o erro mais
 * caro que esta seção pode cometer.
 */
export function ProcessosNoDjen({
  investigadoId,
  nome,
  ehMaster,
  aoMudar,
}: {
  investigadoId: string;
  nome: string;
  ehMaster: boolean;
  /** ⭐ 19/09/2026: a busca lança no caso — quem mostra a lista e a faixa relê. */
  aoMudar?: () => void;
}) {
  const [buscando, setBuscando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [busca, setBusca] = useState<BuscaNoDjen | null>(null);

  /* ⭐ A última busca guardada volta ao abrir — 14/09/2026 ("sempre salvar as
     pesquisas"), com as confirmações já feitas em cada processo. */
  useEffect(() => {
    let vivo = true;
    apiInvestigados
      .ultimaBuscaNoDjen(investigadoId)
      .then((r) => vivo && setBusca(r.busca))
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, [investigadoId]);

  async function buscar() {
    setBuscando(true);
    setErro(null);
    try {
      const r = await apiInvestigados.processosNoDjen(investigadoId);
      setBusca(r.busca);
      /*
       * ⭐⭐ **A busca LANÇA no caso — decisão dele, 19/09/2026.** Os confirmados
       * e os prováveis entram sozinhos; os homônimos, não. A execução contra a
       * parte vai ao passivo; o resto, a Processos. A tela diz o que entrou e
       * onde — lançamento calado é o que faz alguém procurar no lugar errado.
       */
      const l = r.lancados;
      if (l) {
        const partes = [
          l.emProcessos > 0 && `${l.emProcessos} em Processos judiciais relevantes`,
          l.emPassivo > 0 && `${l.emPassivo} em Restrições e passivo (a parte é quem é cobrada)`,
        ].filter(Boolean);
        const resto = [
          l.jaNoCaso > 0 && `${l.jaNoCaso} já ${l.jaNoCaso === 1 ? 'estava' : 'estavam'} no caso`,
          l.descartados > 0 && `${l.descartados} apagado(s) por você não ${l.descartados === 1 ? 'voltou' : 'voltaram'}`,
          l.homonimos > 0 && `${l.homonimos} homônimo(s) ficaram de fora`,
        ].filter(Boolean);
        avisar(
          (partes.length ? `Entraram no caso: ${partes.join(' e ')}.` : 'Nada novo para lançar no caso.') +
            (resto.length ? ` ${resto.join(' · ')}.` : ''),
        );
      }
      aoMudar?.();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'A busca no diário não foi.');
    } finally {
      setBuscando(false);
    }
  }

  return (
    <BuscaDeAtivo
      rotulo={busca ? 'Buscar de novo no diário' : 'Buscar processos'}
      custo={{ tipo: 'gratuita' }}
      buscando={buscando}
      rotuloBuscando="Buscando no diário…"
      erro={erro}
      aoBuscar={() => void buscar()}
      alcance={
        <>
          Diário de Justiça Eletrônico Nacional, do CNJ. <strong>Sem custo</strong>, todos os
          tribunais, desde 2021. ⚠️ A busca é <strong>por nome</strong>: o diário não devolve
          CPF, então o Hub confere a identidade pelo que já sabe do caso e diz o quanto tem
          certeza.
        </>
      }
    >
      {busca && (
        <div className="resultado-djen">
          <p className="ajuda">
            {busca.em && (
              <>
                <strong>Busca guardada de {quandoFoi(busca.em)}</strong> ·{' '}
              </>
            )}
            {busca.totalDePublicacoes} publicações no nome <strong>{nome}</strong> ·{' '}
            {busca.processos.length} processos ·{' '}
            <strong>{busca.resumo.confirmado} confirmado(s)</strong> ·{' '}
            {busca.resumo.provavel} provável(is) · {busca.resumo.homonimo} possível(is) homônimo(s)
          </p>

          {/*
            ⚠️ **O corte é DITO.** Nome comum devolve milhares de publicações e o
            Hub traz as primeiras mil. Calar isso faria a lista parecer completa —
            e "não achei mais" viraria "não há mais".
          */}
          {busca.truncado && (
            <p className="ajuda ressalvas-leitura">
              ⚠️ Há mais publicações do que o Hub trouxe. Esta lista <strong>não é
              completa</strong> — nome muito comum precisa de confirmação por documento.
            </p>
          )}

          {busca.processos.length === 0 ? (
            <p className="ajuda ajuda-solta">
              Nenhuma publicação neste nome. ⚠️ O diário é um <strong>diário</strong>, não um
              cadastro: processo sem publicação desde 2021 não aparece aqui.
              <strong> Isto não é “não há processo”.</strong>
            </p>
          ) : (
            <DoisBlocos processos={busca.processos} investigadoId={investigadoId} ehMaster={ehMaster} />
          )}
        </div>
      )}
    </BuscaDeAtivo>
  );
}

/**
 * A busca PAGA na central notarial — R$ 19,00 por documento.
 *
 * ⛔ **A consulta mais cara do Hub**, acima dos R$ 15,41 do veículo. Segue a
 * mesma regra: só por clique, só do master, **com o valor escrito no botão
 * antes**. Consulta que dispara sem o preço à vista é a que faz a fatura
 * chegar com surpresa.
 *
 * ⭐ **Quando a integração está desligada, a tela DIZ o que falta** em vez de
 * oferecer um botão que vai falhar. E o motivo é específico, porque cada um se
 * resolve num lugar diferente.
 *
 * ⚠️ **`desativada: false` é ALARME, não detalhe.** A fonte cria um
 * monitoramento que cobra a cada 15 a 30 dias; se o Hub não conseguiu
 * desativá-lo, é uma assinatura correndo. A tela grita, e o vigia acha.
 */
export function BuscaNaCentral({ lista }: { lista: Investigado[] }) {
  const [estado, setEstado] = useState<{ disponivel: boolean; motivo: string | null; preco: number } | null>(null);
  const [indo, setIndo] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [achados, setAchados] = useState<Record<string, BuscaDeEscrituras>>({});
  /* ⚠️ Buscar de novo custa de novo: o segundo clique pede confirmação. */
  const [deNovo, setDeNovo] = useState<string | null>(null);

  /* ⭐ A última busca de cada investigado volta guardada — 14/09/2026 ("sempre
     salvar as pesquisas"). São R$ 19,00 por documento: ver de novo não pode
     custar de novo. */
  const ids = lista.map((i) => i.id).join(',');
  useEffect(() => {
    if (lista.length === 0) return;
    let vivo = true;
    void Promise.all(
      lista.map((i) => apiCentralNotarial.disponibilidade(i.id).then((r) => [i.id, r] as const)),
    )
      .then((respostas) => {
        if (!vivo) return;
        const primeira = respostas[0]![1];
        setEstado({ disponivel: primeira.disponivel, motivo: primeira.motivo, preco: primeira.preco });
        const guardadas: Record<string, BuscaDeEscrituras> = {};
        for (const [id, r] of respostas) if (r.guardada) guardadas[id] = r.guardada;
        setAchados(guardadas);
      })
      .catch(() => vivo && setEstado(null));
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids]);

  if (!estado) return null;

  /* ⚠️ O que já foi pago continua à vista mesmo com a busca desligada. */
  const guardadas = lista.filter((i) => achados[i.id]);

  if (!estado.disponivel) {
    return (
      <>
        <p className="ajuda ajuda-solta">
          A busca automática de escrituras e procurações existe e{' '}
          <strong>ainda não está ligada</strong>
          {estado.motivo === 'conta_nao_e_da_kairos' ? (
            <> — a conta declarada <strong>não é a da KAIRÓS</strong>. Ligar assim registraria o
            trabalho no nome de outra empresa, e a nota fiscal sairia no CNPJ errado.</>
          ) : estado.motivo === 'sem_chave' ? (
            <> — falta a chave de aplicação, que se gera no portal em Gerenciamento.</>
          ) : estado.motivo === 'sem_responsavel' ? (
            <> — falta declarar o CPF do responsável, que a central carimba nos documentos.</>
          ) : estado.motivo === 'sem_cnpj_declarado' ? (
            <> — falta declarar de qual CNPJ é a chave.</>
          ) : (
            <> — ela nasce desligada e liga quando a conta da KAIRÓS existir.</>
          )}
        </p>
        {guardadas.length > 0 && (
          <div className="busca-na-central">
            {guardadas.map((i) => (
              <div key={i.id}>
                <p className="ajuda">
                  <strong>{i.nome}</strong>
                </p>
                <AtosDaCentral busca={achados[i.id]!} />
              </div>
            ))}
          </div>
        )}
      </>
    );
  }

  async function buscar(id: string) {
    setIndo(id);
    setErro(null);
    setDeNovo(null);
    try {
      const r = await apiCentralNotarial.buscar(id);
      setAchados((a) => ({ ...a, [id]: r.busca }));
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'A busca não foi.');
    } finally {
      setIndo(null);
    }
  }

  const preco = `R$ ${estado.preco.toFixed(2).replace('.', ',')}`;

  return (
    <div className="busca-na-central">
      <p className="ajuda">
        Índice nacional de escrituras e procurações, a partir de 2006. Devolve tabelionato, livro,
        folha e espécie — <strong>não o teor</strong>, que depende de certidão.{' '}
        <strong>Propõe, não lança:</strong> o índice diz que o ato existe, e não se ele continua
        valendo — procuração revogada consta do mesmo jeito.
      </p>
      {lista.map((i) => (
        <div key={i.id}>
          {deNovo === i.id ? (
            <div className="aviso-de-custo">
              <p>
                Já há uma busca guardada de <strong>{i.nome}</strong>. Buscar de novo cobra{' '}
                <strong>{preco}</strong> outra vez.
              </p>
              <button
                type="button"
                className="botao-primario"
                disabled={indo === i.id}
                onClick={() => void buscar(i.id)}
              >
                Consultar
              </button>
              <button type="button" className="botao-texto" onClick={() => setDeNovo(null)}>
                Cancelar
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="botao-secundario"
              disabled={indo === i.id}
              onClick={() => (achados[i.id] ? setDeNovo(i.id) : void buscar(i.id))}
            >
              {indo === i.id
                ? 'Consultando a central…'
                : achados[i.id]
                  ? `Buscar de novo · ${preco} · ${i.nome}`
                  : `Buscar · ${preco} · ${i.nome}`}
            </button>
          )}

          {achados[i.id] && <AtosDaCentral busca={achados[i.id]!} />}
        </div>
      ))}
      {erro && <p className="erro">{erro}</p>}
    </div>
  );
}

/** Os atos que a central devolveu — da busca de agora ou da guardada. */
function AtosDaCentral({ busca }: { busca: BuscaDeEscrituras }) {
  return (
    <>
      {busca.em && (
        <p className="ajuda">
          <strong>Busca guardada de {quandoFoi(busca.em)}</strong> · {busca.atos.length} ato(s)
        </p>
      )}

      {!busca.desativada && (
        <p className="erro">
          ⛔ A consulta foi criada e <strong>não foi desativada</strong>. Ela cobrará de novo no
          próximo ciclo — desative no portal da central.
        </p>
      )}

      {busca.atos.length === 0 && (
        <p className="ajuda ressalvas-leitura">
          ⚠️ Nada consta neste documento no índice nacional. Isto <strong>não é</strong> "não há
          escritura": o índice começa em 2006.
        </p>
      )}

      {busca.atos.length > 0 && (
        <ul className="atos-da-central">
          {busca.atos.map((a, n) => (
            <li key={n}>
              <span className="selo-grau">{a.tipo === 'Deed' ? 'Escritura' : 'Procuração'}</span>{' '}
              {a.tabelionato.nome ?? '—'}
              {a.tabelionato.cidade && <> · {a.tabelionato.cidade}/{a.tabelionato.uf}</>}
              {' · '}
              <span className="dado">
                livro {a.livro ?? '—'}, folha {a.folha ?? '—'}
              </span>
              {a.data && <> · {a.data.slice(0, 10).split('-').reverse().join('/')}</>}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/**
 * O botão que registra a pesquisa humana na central notarial.
 *
 * ⚠️ **Um registro por investigado**, e não um por capítulo: a consulta na
 * central é **uma** e devolve escritura e procuração juntas. Registrar duas
 * vezes contaria dois levantamentos onde houve um.
 */
/* ────────────────────────────────────────────────────────────────────────────
 * A RELAÇÃO DA CENTRAL — ler o PDF, guardar, e escolher o que extrair
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * Um ato, com as repetições da relação já juntas.
 *
 * ⚠️ **A central repete o mesmo ato**, e isso não é defeito de leitura: no
 * documento real de 13/05/2026 uma procuração aparece **quatro vezes** com o
 * mesmo livro e a mesma folha — provavelmente uma linha por parte do ato.
 *
 * ⛔ **Juntar as repetições aqui é questão de DINHEIRO, não de arrumação.** A
 * cópia de um ato é uma só e se paga uma vez; marcar as quatro linhas faria a
 * conta a levar ao cliente sair quatro vezes maior. **Por isso o pedido vai só
 * para o primeiro `id` do grupo.**
 */
type AtoAgrupado = {
  ids: string[];
  /**
   * ⛔ **Os que estão marcados — e é por eles que se DESMARCA.**
   *
   * A primeira versão marcava `ids[0]` e desmarcava `ids[0]`, o que parecia
   * simétrico e não era: entre linhas idênticas o banco não garantia ordem, e
   * `ids[0]` podia ser outro ato na volta. O Richard clicou e **a caixa não
   * desmarcava**. Hoje a ordem é determinística no servidor **e** a tela mira
   * quem está marcado — as duas coisas, porque uma sozinha só esconde a outra.
   */
  pedidos: string[];
  vezes: number;
  ato: AtoGuardado;
};

function agrupar(atos: AtoGuardado[]): AtoAgrupado[] {
  const por = new Map<string, AtoAgrupado>();
  for (const a of atos) {
    const chave = [a.ocorridoEm, a.cns, a.especie, a.livro, a.folha].join('|');
    const achado = por.get(chave);
    if (achado) {
      achado.ids.push(a.id);
      achado.vezes += 1;
      if (a.pedida) achado.pedidos.push(a.id);
      /* Quem representa o grupo na tela é a linha marcada, se houver — é dela
         que saem o "cópia recebida" e o estado da caixa. */
      if (a.pedida && !achado.ato.pedida) achado.ato = a;
    } else {
      por.set(chave, { ids: [a.id], pedidos: a.pedida ? [a.id] : [], vezes: 1, ato: a });
    }
  }
  return [...por.values()];
}

/**
 * A relação da central para um investigado: subir, olhar e escolher.
 *
 * ## ⚠️ Por que ela ocupa tanto espaço na tela
 *
 * Porque o documento real traz **193 atos**, e cada um deles é uma decisão de
 * gastar dinheiro do cliente. Uma lista que não deixasse filtrar e comparar
 * empurraria a escolha para o cansaço.
 *
 * ## A ordem é do mais NOVO para o mais velho, e é escolha
 *
 * ⚠️ O papel vem em ordem cronológica, e o **anexo** do relatório vai sair
 * assim — é o que reconstrói a evolução patrimonial. Mas aqui a pergunta é
 * outra: *de quais vale pedir cópia?* E aí o recente pesa mais, porque é o que
 * está perto do surgimento da dívida.
 */
export function PesquisaDaCentral({
  investigado,
  categoria,
}: {
  investigado: Investigado;
  categoria: CategoriaDeAtivo;
}) {
  const [pesquisa, setPesquisa] = useState<PesquisaGuardada | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [subindo, setSubindo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ressalvas, setRessalvas] = useState<string[]>([]);
  const [ano, setAno] = useState<string>('todos');
  const [cns, setCns] = useState<string>('todos');
  const [soPedidas, setSoPedidas] = useState(false);
  const [apagando, setApagando] = useState(false);
  const [anexando, setAnexando] = useState<string | null>(null);
  const [lendo, setLendo] = useState<string | null>(null);
  const [proposta, setProposta] = useState<{
    atoId: string;
    leitura: LeituraDoAto;
    texto: string;
  } | null>(null);

  /*
   * ⭐ **A espécie virou FILTRO em 08/09/2026** — antes ela saía do capítulo.
   *
   * > *"Sugere-se que seja mantido apenas 01 tópico ATOS NOTARIAIS onde iremos
   * > incluir os documentos e o próprio sistema faz essa distinção."* — Richard
   *
   * ⚠️ **Nasce em "todas", e isso é o ponto.** Uma pesquisa da central traz as
   * duas espécies no mesmo documento — 159 escrituras e 34 procurações no
   * exemplo real. Filtrar por padrão esconderia metade do que ele acabou de
   * subir, que é o oposto do pedido.
   */
  const [especie, setEspecie] = useState<'todas' | 'escritura' | 'procuracao'>('todas');

  useEffect(() => {
    let vivo = true;
    void (async () => {
      try {
        const r = await apiCentralNotarial.pesquisas(investigado.id);
        if (vivo) setPesquisa(r.pesquisas[0] ?? null);
      } catch {
        /* Silêncio aqui é aceitável: a ausência de pesquisa é o estado normal,
           e o erro real aparece quando ele tentar subir. */
      } finally {
        if (vivo) setCarregando(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [investigado.id]);

  async function subir(arquivo: File) {
    setSubindo(true);
    setErro(null);
    setAviso(null);
    setRessalvas([]);
    try {
      const r = await apiCentralNotarial.lerPesquisa(investigado.id, arquivo);
      setPesquisa(r.pesquisa ?? null);
      setRessalvas(r.ressalvas ?? []);
      if (r.marcacoesHerdadas && r.marcacoesHerdadas > 0) {
        setAviso(
          `${r.marcacoesHerdadas} cópia(s) que você já tinha pedido na pesquisa anterior ` +
            'continuam marcadas aqui.',
        );
      }
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui ler a pesquisa.');
    } finally {
      setSubindo(false);
    }
  }

  async function pedir(ids: string[], querer: boolean) {
    setErro(null);
    try {
      const r = await apiCentralNotarial.pedirCopias(investigado.id, ids, querer);
      if (r.pesquisa) setPesquisa(r.pesquisa);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui marcar.');
    }
  }

  /**
   * A cópia chegou: anexa, e o Hub faz as três coisas de uma vez.
   *
   * ⚠️ **A resposta é contada em voz alta na tela**, e não só gravada: o que
   * foi lançado no Fundo de Custas é dinheiro do cliente, e lançamento que
   * acontece em silêncio é lançamento que ninguém confere.
   */
  async function anexar(atoId: string, arquivo: File) {
    setAnexando(atoId);
    setErro(null);
    setAviso(null);
    setRessalvas([]);
    try {
      const r = await apiCentralNotarial.anexarCopia(investigado.id, atoId, arquivo);
      if (r.pesquisa) setPesquisa(r.pesquisa);
      const partes: string[] = ['Cópia anexada e conferida com o ato.'];
      if (r.lancadoNoFundo) {
        partes.push('O custo da certidão foi lançado no Fundo de Custas do caso.');
      } else {
        partes.push(
          'A capa desta certidão não traz o valor — lance o custo à mão no Fundo de Custas.',
        );
      }
      if (r.atoEhImagem) {
        partes.push('O ato veio como imagem, então o resumo dele ainda depende de leitura sua.');
      }
      setAviso(partes.join(' '));
      setRessalvas([...(r.leitura?.ressalvas ?? []), ...(r.avisosDoFundo ?? [])]);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui anexar a cópia.');
    } finally {
      setAnexando(null);
    }
  }

  /**
   * Lê o ato de uma cópia anexada e **propõe** o resumo.
   *
   * ⚠️ **Nada é gravado aqui.** A proposta abre num painel onde ele lê, corrige
   * e aceita — porque nome lido errado não tem dígito verificador que o denuncie.
   */
  async function ler(atoId: string) {
    setLendo(atoId);
    setErro(null);
    try {
      const r = await apiCentralNotarial.lerAto(investigado.id, atoId);
      setProposta({ atoId, leitura: r.leituraDoAto, texto: r.leituraDoAto.resumo });
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui ler o ato.');
    } finally {
      setLendo(null);
    }
  }

  async function aceitarResumo() {
    if (!proposta) return;
    setErro(null);
    try {
      const r = await apiCentralNotarial.salvarResumo(investigado.id, proposta.atoId, proposta.texto);
      if (r.pesquisa) setPesquisa(r.pesquisa);
      setProposta(null);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui gravar o resumo.');
    }
  }

  async function apagar() {
    if (!pesquisa) return;
    setErro(null);
    try {
      await apiCentralNotarial.apagarPesquisa(investigado.id, pesquisa.id);
      setPesquisa(null);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui apagar.');
    }
  }

  if (carregando) return null;

  if (!pesquisa) {
    return (
      <div className="pesquisa-central">
        <p className="rotulo">{investigado.nome}</p>
        <p className="ajuda">
          Pesquisou a central e salvou o documento? <strong>Suba o PDF aqui</strong> e o Hub lê
          a relação inteira — data, tabelionato, município, livro e folha de cada ato. A relação
          fica guardada: é ela que vai aos anexos do relatório, e é dela que se escolhe de quais
          atos pedir cópia.
        </p>
        {/* ⭐ Linha rápida, 18/09/2026: o botão do desenho novo no lugar do campo
            cru do navegador, que dizia "Choose File · No file chosen" em inglês.
            O mesmo arquivo, pelo mesmo caminho: escolher ainda sobe na hora. */}
        <label className={`imv-bt lr-arquivo${subindo ? ' lr-ocupado' : ''}`}>
          <Icone nome="subir" tamanho={14} />
          {subindo ? 'Lendo o documento…' : 'Subir o PDF da relação'}
          <input
            type="file"
            accept="application/pdf"
            disabled={subindo}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void subir(f);
              e.target.value = '';
            }}
          />
        </label>
        {erro && <p className="erro">{erro}</p>}

        {/*
          ⚠️ **A saída para quem pesquisou e não vai subir o PDF.** Sem ela, a
          ressalva de escrituras e procurações não sairia no documento — e um
          capítulo vazio leria-se como "não há ato lavrado", quando o que houve
          foi uma busca com alcance próprio.

          ⭐ Fica **abaixo** do envio, e não ao lado: subir o documento é o
          caminho bom, porque registra a pesquisa **e** guarda a relação.
        */}
        <RegistrarCentralNotarial investigado={investigado} />
      </div>
    );
  }

  /* ⚠️ **Todos os atos, e o filtro decide o que se vê.** A contagem e os
     seletores abaixo derivam daqui, então tirar a espécie do conjunto faria os
     filtros de ano e tabelionato mentirem sobre o que existe. */
  const doCapitulo = pesquisa.atos;
  const anos = [...new Set(doCapitulo.map((a) => a.ocorridoEm.slice(0, 4)))].sort().reverse();
  const cartorios = [...new Map(doCapitulo.map((a) => [a.cns, a] as const)).values()].sort((a, b) =>
    a.cartorio.localeCompare(b.cartorio),
  );

  const visiveis = agrupar(
    doCapitulo.filter(
      (a) =>
        (especie === 'todas' || a.especie === especie) &&
        (ano === 'todos' || a.ocorridoEm.startsWith(ano)) &&
        (cns === 'todos' || a.cns === cns) &&
        (!soPedidas || a.pedida),
    ),
  ).sort((a, b) => b.ato.ocorridoEm.localeCompare(a.ato.ocorridoEm));

  const pedidasNoCapitulo = agrupar(doCapitulo).filter((g) => g.pedidos.length > 0).length;
  const naoPedidasVisiveis = visiveis.filter((g) => g.pedidos.length === 0);

  return (
    <div className="pesquisa-central">
      <p className="rotulo">{investigado.nome}</p>

      <p className="ajuda">
        Relação pesquisada em <strong>{paraTela(pesquisa.pesquisadaEm)}</strong> —{' '}
        {pesquisa.resumo.escrituras} escritura(s) e {pesquisa.resumo.procuracoes} procuração(ões),
        de {pesquisa.resumo.primeiroAto ? paraTela(pesquisa.resumo.primeiroAto) : '—'} a{' '}
        {pesquisa.resumo.ultimoAto ? paraTela(pesquisa.resumo.ultimoAto) : '—'}, em{' '}
        {pesquisa.resumo.cartorios} tabelionato(s).
      </p>

      {/*
        ⚠️ **O alcance da relação sai escrito, e não é enfeite.** O índice
        começa em 2006: ato lavrado antes não consta, e um capítulo que não
        dissesse isso leria-se como "não existe ato anterior".
      */}
      <p className="ajuda">
        Alcança atos lavrados a partir de 1º de janeiro de 2006, e até a data da pesquisa. O
        índice devolve o tabelionato, o livro e a folha — <strong>não o teor do ato</strong>, que
        depende da cópia.
      </p>

      {aviso && <p className="ajuda ressalvas-leitura">{aviso}</p>}
      {ressalvas.map((r) => (
        <p key={r} className="ajuda ressalvas-leitura">
          {r}
        </p>
      ))}

      <div className="pesquisa-central-conta">
        <span>
          <strong>{pedidasNoCapitulo}</strong> cópia(s) marcada(s) neste capítulo
        </span>
        {/*
          ⭐ **A conta a levar ao cliente — 30/08/2026.** O preço é de LEI: item 5
          da tabela de emolumentos de São Paulo, fixado pela Lei 11.331/2002 e
          igual em todo o estado.

          ⚠️ **Estimativa, e a faixa acompanha o número de propósito** — o total
          muda com o ISS do município, e cartório de outro estado tem outra lei.
          Número exato onde há estimativa é o que faz alguém prometer errado.
        */}
        {pesquisa.resumo.aPedir > 0 && (
          <span>
            Cópias a pedir: <Valor de={pesquisa.resumo.estimativa.estimado} /> estimados —
            entre <Valor de={pesquisa.resumo.estimativa.minimo} /> e{' '}
            <Valor de={pesquisa.resumo.estimativa.maximo} />, conforme o município
          </span>
        )}
        {pesquisa.resumo.custoConhecido > 0 && (
          <span>
            Cópias já recebidas: <Valor de={pesquisa.resumo.custoConhecido} /> lançados no
            Fundo de Custas
          </span>
        )}
        <span className="ajuda">
          Valor de tabela por ato, em São Paulo. Cartório de outro estado tem tabela própria.
        </span>
      </div>

      <div className="pesquisa-central-filtros">
        {/*
          ⭐ **Vem PRIMEIRO porque é o recorte mais grosso** — antes de ano e
          tabelionato, quem procura decide se quer escritura ou procuração.
        */}
        <label className="imv-campo">
          <span className="imv-rotulo">Espécie</span>
          <span className="imv-in">
            <select
              value={especie}
              onChange={(e) => setEspecie(e.target.value as 'todas' | 'escritura' | 'procuracao')}
            >
              <option value="todas">
                Todas ({pesquisa.resumo.escrituras + pesquisa.resumo.procuracoes})
              </option>
              <option value="escritura">Escrituras ({pesquisa.resumo.escrituras})</option>
              <option value="procuracao">Procurações ({pesquisa.resumo.procuracoes})</option>
            </select>
          </span>
        </label>
        <label className="imv-campo">
          <span className="imv-rotulo">Ano</span>
          <span className="imv-in">
            <select value={ano} onChange={(e) => setAno(e.target.value)}>
              <option value="todos">Todos</option>
              {anos.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </span>
        </label>
        <label className="imv-campo">
          <span className="imv-rotulo">Tabelionato</span>
          <span className="imv-in">
            <select value={cns} onChange={(e) => setCns(e.target.value)}>
              <option value="todos">Todos</option>
              {cartorios.map((c) => (
                <option key={c.cns} value={c.cns}>
                  {c.cartorio} · {c.municipio}/{c.uf}
                </option>
              ))}
            </select>
          </span>
        </label>
        <label className="pesquisa-central-so-pedidas">
          <input type="checkbox" checked={soPedidas} onChange={(e) => setSoPedidas(e.target.checked)} />
          <span>Só as marcadas</span>
        </label>
      </div>

      {naoPedidasVisiveis.length > 0 && (
        <button
          type="button"
          className="botao-texto"
          onClick={() => void pedir(naoPedidasVisiveis.map((g) => g.ids[0]!), true)}
        >
          Marcar os {naoPedidasVisiveis.length} atos que estão à vista
        </button>
      )}

      <ul className="lista-de-atos">
        {visiveis.map((g) => (
          <li key={g.ids[0]} className={g.ato.pedida ? 'ato-notarial pedida' : 'ato-notarial'}>
            <label>
              <input
                type="checkbox"
                checked={g.pedidos.length > 0}
                disabled={g.ato.chegou}
                /* ⚠️ Marcar pede UMA cópia; desmarcar solta TODAS as linhas
                   marcadas do grupo — senão uma sobra e a caixa volta sozinha. */
                onChange={(e) =>
                  void pedir(e.target.checked ? [g.ids[0]!] : g.pedidos, e.target.checked)
                }
              />
              <span className="ato-linha">
                <span className="dado">{paraTela(g.ato.ocorridoEm)}</span>
                {/*
                  ⭐ **A ESPÉCIE sai escrita em cada linha — 08/09/2026.**
                  Enquanto havia um capítulo para cada, o título dizia qual era.
                  Com as duas no mesmo tópico, a linha precisa dizer — e é a
                  diferença que decide o que o ato prova: escritura pode revelar
                  bem, procuração **não transfere propriedade**.
                */}
                <span className="selo-especie">
                  {g.ato.especie === 'escritura' ? 'Escritura' : 'Procuração'}
                </span>
                <span>
                  {g.ato.cartorio} · {g.ato.municipio}/{g.ato.uf}
                </span>
                <span className="dado">
                  Livro {g.ato.livro} · folha {g.ato.folha}
                </span>
                {g.vezes > 1 && (
                  <span className="ato-vezes" title="A central lista este mesmo ato mais de uma vez">
                    consta {g.vezes}×
                  </span>
                )}
                {/*
                  ⭐ **O anexo só aparece no ato JÁ MARCADO e ainda sem cópia.**
                  Oferecê-lo em todos poria um botão de anexar em 193 linhas, e
                  a cópia só existe para quem foi pedida.
                */}
                {g.pedidos.length > 0 && !g.ato.chegou && (
                  <label className="ato-anexar">
                    <span>a cópia chegou — anexar</span>
                    <input
                      type="file"
                      accept="application/pdf"
                      disabled={anexando === g.ids[0]}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) void anexar(g.pedidos[0] ?? g.ids[0]!, f);
                        e.target.value = '';
                      }}
                    />
                  </label>
                )}
                {g.ato.chegou && <span className="ato-chegou">cópia recebida</span>}
                {g.ato.chegou && g.ato.custo !== null && <Valor de={g.ato.custo} />}
                {g.ato.chegou && !g.ato.resumo && (
                  <button
                    type="button"
                    className="botao-texto"
                    onClick={(e) => {
                      e.preventDefault();
                      void ler(g.pedidos[0] ?? g.ids[0]!);
                    }}
                  >
                    {lendo === (g.pedidos[0] ?? g.ids[0]) ? 'lendo o ato…' : 'ler o ato'}
                  </button>
                )}
              </span>
              {g.ato.resumo && <span className="ato-resumo">{g.ato.resumo}</span>}
            </label>
          </li>
        ))}
      </ul>

      {visiveis.length === 0 && (
        <p className="ajuda">Nenhum ato desta espécie com os filtros escolhidos.</p>
      )}

      {erro && <p className="erro">{erro}</p>}

      {/*
        ── A PROPOSTA DE RESUMO DO ATO — 30/08/2026 ─────────────────────────
        ⛔ **Nada aqui está gravado.** O leitor propõe; ele lê, corrige e aceita.
        ⚠️ **E os VALORES vêm em lista, sem um escolhido** — um ato traz o preço,
        a base do imposto, os emolumentos e às vezes o valor de um ato antigo
        citado dentro. A leitura tentou eleger e errou por 27 milhões.
      */}
      {proposta && (
        <div className="proposta-do-ato">
          <p className="lr-titulo">O que a leitura do ato encontrou</p>
          <p className="ajuda">
            Lido {proposta.leitura.origem === 'imagem' ? 'por reconhecimento de imagem' : 'do texto do documento'}
            {proposta.leitura.origem === 'imagem' && ' — o ato veio como reprodução do livro'}.{' '}
            <strong>Nada está gravado</strong> até você aceitar.
          </p>

          {proposta.leitura.objeto && (
            <p>
              <span className="lr-dado-rotulo">Objeto</span>
              {proposta.leitura.objeto}
            </p>
          )}

          <p>
            <span className="lr-dado-rotulo">Documentos conferidos pelo dígito</span>
            {proposta.leitura.documentos.length}
            {proposta.leitura.matriculas.length > 0 &&
              ` · matrícula(s) ${proposta.leitura.matriculas.join(', ')}`}
          </p>

          {proposta.leitura.poderes.length > 0 && (
            <p>
              <span className="lr-dado-rotulo">Poderes identificados</span>
              {proposta.leitura.poderes.join(' · ')}
            </p>
          )}

          {proposta.leitura.valores.length > 0 && (
            <>
              <p className="lr-dado-rotulo">Valores que aparecem no ato — nenhum foi escolhido</p>
              <ul className="valores-do-ato">
                {proposta.leitura.valores.map((v) => (
                  <li key={`${v.valor}-${v.trecho.slice(0, 20)}`}>
                    <Valor de={v.valor} />
                    <span className="ajuda">…{v.trecho}</span>
                  </li>
                ))}
              </ul>
            </>
          )}

          {proposta.leitura.ressalvas.map((r) => (
            <p key={r} className="ajuda ressalvas-leitura">
              {r}
            </p>
          ))}

          <label className="imv-campo">
            <span className="imv-rotulo">O resumo, para você corrigir antes de aceitar</span>
            <span className="imv-in lr-texto">
              <textarea
                value={proposta.texto}
                rows={4}
                onChange={(e) => setProposta({ ...proposta, texto: e.target.value })}
              />
            </span>
          </label>

          <div className="lr-acoes">
            <button type="button" className="imv-bt fantasma" onClick={() => setProposta(null)}>
              Descartar
            </button>
            <button type="button" className="imv-bt primario" onClick={() => void aceitarResumo()}>
              <Icone nome="check" tamanho={14} /> Aceitar este resumo
            </button>
          </div>
        </div>
      )}

      {/* ⛔ Só aparece enquanto ninguém pediu cópia nenhuma: com trabalho em
          cima, apagar levaria junto a decisão de extrair e o rastro do gasto. */}
      {pesquisa.resumo.pedidas === 0 && (
        <button type="button" className="botao-texto" onClick={() => setApagando(true)}>
          Subiu o arquivo errado? Apagar esta relação
        </button>
      )}
      {apagando && (
        <Confirmar
          titulo="Apagar esta relação?"
          descricao="A relação some do Hub, e com ela o anexo que iria ao relatório. Só faça isto se o arquivo subiu errado."
          rotuloAcao="Apagar a relação"
          aoConfirmar={() => {
            setApagando(false);
            void apagar();
          }}
          aoFechar={() => setApagando(false)}
        />
      )}
    </div>
  );
}

function RegistrarCentralNotarial({ investigado }: { investigado: Investigado }) {
  const [feito, setFeito] = useState(false);
  const [indo, setIndo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function registrar() {
    setIndo(true);
    setErro(null);
    try {
      await apiCentralNotarial.registrar(investigado.id);
      setFeito(true);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui registrar.');
    } finally {
      setIndo(false);
    }
  }

  return (
    <div className="registrar-central">
      <p className="ajuda">
        Pesquisou e <strong>não vai subir o documento</strong>? Registre aqui só que a busca
        aconteceu. É o que faz a ressalva sair no relatório — sem ela, um capítulo vazio
        parece afirmar que não há ato lavrado, quando o que houve foi uma busca com alcance
        próprio.
      </p>
      <button type="button" className="imv-bt" disabled={indo || feito} onClick={() => void registrar()}>
        {feito ? <><Icone nome="check" tamanho={13} /> Pesquisa registrada</> : indo ? 'Registrando…' : 'Registrar só a pesquisa'}
      </button>
      {/* ⚠️ O botão fica desabilitado DEPOIS de clicado, e a linha diz por quê —
          antes ele apagava sem explicação, e parecia defeito. */}
      {feito && (
        <p className="ajuda">
          Registrado. Um registro por pesquisa basta: a ressalva sai igual, e dois registros
          fariam um levantamento parecer dois.
        </p>
      )}
      {erro && <p className="erro">{erro}</p>}
    </div>
  );
}

/**
 * A lista em **dois blocos** — decisão do Richard, 29/08/2026.
 *
 * ## Por que dois blocos, e não um filtro com botões
 *
 * A busca de um investigado do caso real devolveu **38 processos** numa lista
 * ordenada por confiança, sem nada apontando quais importam. Dentro dela havia
 * três incidentes de desconsideração e um arrolamento de herança — os achados
 * mais decisivos do lote — no meio de agravos e apelações.
 *
 * ⚠️ **Botão de filtro exige lembrar de clicar.** O que importa tem de estar na
 * frente sem pedir nada de quem abre a tela; o resto fica a um clique.
 *
 * ⛔ **NADA SOME.** O segundo bloco mostra todos os demais, com o tipo escrito.
 * Isto é o que torna aceitável a régua de palavras escritas à mão em
 * `processos-pelo-djen.ts`: o tipo de ação vem como texto livre do CNJ, então
 * um tipo que ninguém previu cai no segundo bloco — e continua visível. **A
 * régua ordena; ela não esconde.**
 *
 * ⚠️ **O segundo bloco nasce FECHADO, e a contagem fica no rótulo.** Gaveta sem
 * número na tampa é gaveta que ninguém abre.
 */
function DoisBlocos({
  processos,
  investigadoId,
  ehMaster,
}: {
  processos: ProcessoAchado[];
  investigadoId: string;
  ehMaster: boolean;
}) {
  const peso: Record<GrauDeCerteza, number> = { confirmado: 0, provavel: 1, homonimo: 2 };
  const ordenar = (lista: ProcessoAchado[]) =>
    [...lista].sort(
      (a, b) =>
        peso[a.grau] - peso[b.grau] ||
        maisRaro(a) - maisRaro(b) ||
        (b.ultimaEm ?? '').localeCompare(a.ultimaEm ?? ''),
    );

  const interessam = ordenar(processos.filter((p) => p.interesse.length > 0));
  const demais = ordenar(processos.filter((p) => p.interesse.length === 0));

  return (
    <>
      {interessam.length > 0 && (
        <>
          <p className="secao-subtitulo">
            O que interessa ao caso · {interessam.length} de {processos.length}
          </p>
          <ul className="lista-de-processos">
            {interessam.map((p) => (
              <UmProcesso key={p.numero} processo={p} investigadoId={investigadoId} ehMaster={ehMaster} />
            ))}
          </ul>
        </>
      )}

      {demais.length > 0 && (
        <details className="gaveta">
          <summary>
            {interessam.length === 0
              ? `Nenhum processo se encaixou na régua — ver os ${demais.length}`
              : `Os demais · ${demais.length} processo${demais.length > 1 ? 's' : ''}`}
          </summary>
          <div className="gaveta-corpo">
            {/*
              ⚠️ **A ressalva fica DENTRO da gaveta, onde ela é lida.** Quem abre
              este bloco está conferindo se a régua deixou algo passar — é
              exatamente aqui que ele precisa saber que a régua é uma lista de
              palavras, e não uma classificação oficial.
            */}
            <p className="ajuda">
              Recurso, incidente e o que a régua não reconheceu. ⚠️ O tipo de ação vem como
              <strong> texto livre</strong> do diário: um tipo não previsto cai aqui.
              <strong> Confira antes de concluir que não há nada.</strong>
            </p>
            <ul className="lista-de-processos">
              {demais.map((p) => (
                <UmProcesso key={p.numero} processo={p} investigadoId={investigadoId} ehMaster={ehMaster} />
              ))}
            </ul>
          </div>
        </details>
      )}
    </>
  );
}

/**
 * Um processo achado no diário, com o **destravar** de R$ 0,20.
 *
 * ⚠️ **O botão só aparece no que ainda não está confirmado.** Oferecer confirmação
 * paga para o que o documento já confirmou seria vender o que já se tem.
 *
 * ⚠️ **E `inconclusivo` não rebaixa o processo.** Se o tribunal não devolve o
 * documento das partes, o caminho não confirma — mas também não desmente. O
 * grau original fica, e a tela diz por quê.
 */
function UmProcesso({
  processo,
  investigadoId,
  ehMaster,
}: {
  processo: ProcessoAchado;
  investigadoId: string;
  ehMaster: boolean;
}) {
  const [confirmando, setConfirmando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  /* ⭐ A confirmação já feita volta guardada — 14/09/2026. */
  const [conf, setConf] = useState<(ConfirmacaoDeProcesso & { em?: string }) | null>(
    processo.confirmacao ?? null,
  );

  async function confirmar() {
    setConfirmando(true);
    setErro(null);
    try {
      const r = await apiInvestigados.confirmarProcesso(investigadoId, processo.numero);
      setConf(r.confirmacao);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'A confirmação não foi.');
    } finally {
      setConfirmando(false);
    }
  }

  /* O grau que vale na tela: o da confirmação, quando ela concluiu. */
  const grau =
    conf?.resultado === 'confirmado'
      ? 'confirmado'
      : conf?.resultado === 'de_outra_pessoa'
        ? 'homonimo'
        : processo.grau;

  return (
    <li className={`processo grau-${grau}`}>
      <div className="processo-topo">
        <span className={`selo-grau selo-${grau}`}>
          {conf?.resultado === 'de_outra_pessoa' ? 'De outra pessoa' : SELO_DO_GRAU[grau]}
        </span>
        <span className="dado">{processo.numeroComMascara ?? processo.numero}</span>
        <span>{processo.tribunal}</span>
        {/*
          ⚠️ **A posição vem traduzida do servidor.** Aqui havia
          `polo === 'A' ? 'polo ativo' : 'polo passivo'` — e a fonte manda três
          letras, não duas: os processos de TERCEIRO interessado apareciam
          rotulados como réu. Afirmação errada sobre a posição de alguém num
          processo, dentro do que fundamenta um relatório assinado.
        */}
        <span>{DIZER_A_POSICAO[processo.posicao]}</span>
        {/* ⭐ 19/09/2026: onde este processo está no caso (a busca lança os confirmados e os prováveis). */}
        {processo.descartado ? (
          <span className="selo-no-caso apagado">apagado do caso — não volta</span>
        ) : processo.noCaso ? (
          <span className="selo-no-caso">
            no caso · {processo.noCaso === 'passivo' ? 'Restrições e passivo' : 'Processos'}
          </span>
        ) : null}
      </div>

      {/*
        ⚠️ **O motivo, escrito.** Sem ele o primeiro bloco diria "interessa" e
        obrigaria a abrir cada linha para descobrir por quê — o trabalho que o
        bloco existe para poupar. E é o que mantém à vista que ali convivem o
        que ele TEM e o que ele DEVE.
      */}
      {processo.interesse.length > 0 && (
        <p className="motivos-do-processo">
          {processo.interesse.map((m) => (
            <span key={m} className={`selo-motivo motivo-${m}`}>{DIZER_O_MOTIVO[m]}</span>
          ))}
        </p>
      )}

      {processo.classe && (
        <p className="processo-classe">
          {processo.classe}
          {processo.orgao && <> · {processo.orgao}</>}
        </p>
      )}
      <p className="ajuda">
        {conf?.em && <>Confirmado no tribunal em {quandoFoi(conf.em)} · </>}
        {conf?.porQue ?? processo.porQue}
      </p>
      {!conf && processo.confirmadoAntesEm && (
        <p className="ajuda">
          Já consultado no tribunal em {quandoFoi(processo.confirmadoAntesEm)}, antes de a resposta
          ser guardada — confirmar de novo custa de novo.
        </p>
      )}

      {/*
        ⚠️ **O preço vai escrito no botão, antes do clique.** É a regra dos outros
        dois botões pagos da casa: consulta que dispara sem o valor à vista é a
        que faz a fatura chegar com surpresa.
      */}
      {ehMaster && processo.grau !== 'confirmado' && !conf && (
        <button
          type="button"
          className="botao-secundario"
          disabled={confirmando}
          onClick={() => void confirmar()}
        >
          {confirmando ? 'Confirmando no tribunal…' : 'Confirmar identidade · R$ 0,20'}
        </button>
      )}

      {conf?.partes && conf.partes.length > 0 && (
        <ul className="partes-do-processo">
          {conf.partes.slice(0, 8).map((x, i) => (
            <li key={`${x.nome}-${i}`}>
              {x.nome}
              {x.documento && <> · <span className="dado">{x.documento}</span></>}
            </li>
          ))}
        </ul>
      )}

      {/* ⚠️ O custo sai do que a FONTE declarou, nunca de constante da tela. */}
      {conf?.cobrado && <p className="ajuda">Consulta cobrada: R$ {conf.custo.replace('.', ',')}</p>}
      {erro && <p className="erro">{erro}</p>}
    </li>
  );
}

export function Formulario({
  investigadoId,
  categoria,
  aoLancar,
  corrigindo,
  partindoDe,
  aoCancelar,
}: {
  investigadoId: string;
  categoria: CategoriaDeAtivo;
  /** ⭐ Recebe o bem criado — é por ele que os ônus da leitura são lançados. */
  aoLancar: (criado?: AtivoDoInvestigado) => void;
  /**
   * ⭐⭐ **O bem a CORRIGIR — 10/09/2026.** Ausente = lançamento novo, como
   * sempre foi.
   *
   * ⛔ **Corrigir não existia**, e a matrícula que ele estava transcrevendo é um
   * escaneado ruim: um dígito errado obrigava a apagar tudo e redigitar — **e
   * apagar leva os ônus junto**, porque eles pendem do bem.
   *
   * ⭐ **É o MESMO formulário**, e não uma cópia. Dois formulários iguais
   * divergiriam no primeiro campo novo — e a ficha de imóvel vai ganhar seis.
   */
  corrigindo?: AtivoDoInvestigado | null;
  /**
   * ⭐⭐ **OS VALORES DE PARTIDA — 10/09/2026.**
   *
   * Vêm da matrícula lida no chat. ⚠️ **Não é `corrigindo`**, e a distinção é o
   * que o `if` de gravar usa: partindo daqui, o formulário **cria**; partindo
   * de `corrigindo`, ele **corrige** um lançamento que já existe.
   *
   * ⭐ **É o MESMO formulário, de novo.** Uma tela de revisão separada seria a
   * terceira cópia dos mesmos vinte campos — e a primeira correção feita numa
   * só as faria divergir. Aqui ele já revê e corrige antes de lançar, que é
   * melhor que lançar e corrigir depois.
   */
  partindoDe?: MatriculaProposta | null;
  aoCancelar?: () => void;
}) {
  const opcoes = CLASSIFICACOES[categoria];
  /* ⚠️ **Uma fonte só para os valores iniciais.** Corrigir e partir de uma
     leitura preenchem os mesmos campos; só o gravar difere. */
  /*
   * ⚠️ **A conversão mora aqui, e é a única do arquivo.** As duas fontes têm os
   * mesmos campos com os mesmos nomes; o que difere é `especie` (a matrícula
   * não tem) e `onus` (a leitura traz, e o formulário não lança gravame). Os
   * dois caem em `undefined`, e o padrão vale — que é o comportamento certo.
   */
  const inicial = (corrigindo ?? partindoDe ?? null) as
    | (Partial<AtivoDoInvestigado> & Partial<MatriculaProposta>)
    | null;

  const [classificacao, setClassificacao] = useState<ClassificacaoDoAtivo>(
    inicial?.classificacao ?? opcoes[0]!,
  );
  /* ⚠️ Só bem móvel tem espécie — o servidor recusa nos dois sentidos. */
  const [especie, setEspecie] = useState<EspecieDeVeiculo>(inicial?.especie ?? 'automovel');
  const [descricao, setDescricao] = useState(inicial?.descricao ?? '');
  const [identificacao, setIdentificacao] = useState(inicial?.identificacao ?? '');
  /* ⚠️ Número vira texto com VÍRGULA — é como ele digita, e é o que `numero()`
     desfaz na volta. Pôr ponto aqui faria a correção mudar o valor sozinha. */
  const emCampo = (v: number | null | undefined) =>
    v === null || v === undefined ? '' : String(v).replace('.', ',');
  /*
   * ⭐⭐ **As várias avaliações do bem — decisão dele, 15/09/2026.** Vêm da lista
   * gravada, ou da proposta; sem lista, a avaliação única vira a primeira
   * linha, marcada. Uma, no máximo, entra na conta.
   */
  const [avaliacoes, setAvaliacoes] = useState<LinhaDeAvaliacao[]>(() => {
    const lista = inicial?.avaliacoes ?? [];
    if (lista.length > 0) {
      return lista.map((a) => ({
        id: a.id,
        modalidade: a.modalidade ?? '',
        valor: a.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        referencia: a.referencia ?? '',
        avaliadaEm: a.avaliadaEm ?? '',
        conta: a.entraNaConta,
      }));
    }
    if (inicial?.avaliacao !== null && inicial?.avaliacao !== undefined) {
      return [{ modalidade: inicial.modalidadeDaAvaliacao ?? '', valor: emCampo(inicial.avaliacao), referencia: '', avaliadaEm: '', conta: true }];
    }
    return [];
  });
  const [percentual, setPercentual] = useState(emCampo(inicial?.percentual));
  const [valorDeclarado, setValorDeclarado] = useState(emCampo(inicial?.valorDeclarado));
  const [ocorridoEm, setOcorridoEm] = useState(inicial?.ocorridoEm ?? '');
  const [transmitente, setTransmitente] = useState(inicial?.transmitente ?? '');
  const [adquirente, setAdquirente] = useState(inicial?.adquirente ?? '');
  const [vinculo, setVinculo] = useState(inicial?.vinculoEntrePartes ?? '');
  const [proprietarioAtual, setProprietarioAtual] = useState(inicial?.proprietarioAtual ?? '');
  const [conexao, setConexao] = useState(inicial?.elementoDeConexao ?? '');
  const [tipoDoImovel, setTipoDoImovel] = useState(inicial?.tipoDoImovel ?? '');
  const [cidade, setCidade] = useState(inicial?.cidade ?? '');
  const [uf, setUf] = useState(inicial?.uf ?? '');
  /* ⚠️ Medida com VÍRGULA, como ele digita — e como `numero()` desfaz na volta
     (15/09/2026: a leitura punha "98.5" no campo). */
  const emTexto = (n: number | null | undefined) =>
    n === null || n === undefined ? '' : String(n).replace('.', ',');
  const [areaPrivativa, setAreaPrivativa] = useState(emTexto(inicial?.areaPrivativa));
  const [areaDoTerreno, setAreaDoTerreno] = useState(emTexto(inicial?.areaDoTerreno));
  const [areaConstruida, setAreaConstruida] = useState(emTexto(inicial?.areaConstruida));
  const [modalidadeDeAquisicao, setModalidadeDeAquisicao] = useState(
    inicial?.modalidadeDeAquisicao ?? '',
  );
  const [certidaoEm, setCertidaoEm] = useState(inicial?.certidaoEm ?? '');
  const [tabelionato, setTabelionato] = useState(inicial?.tabelionato ?? '');
  const [papelNoAto, setPapelNoAto] = useState<PapelNoAtoNotarial | ''>(
    inicial?.papelNoAto ?? '',
  );
  const [efeito, setEfeito] = useState<EfeitoPatrimonialDoAto | ''>(
    inicial?.efeitoPatrimonial ?? '',
  );
  const [poderes, setPoderes] = useState(inicial?.poderes ?? '');
  const [prazo, setPrazo] = useState(inicial?.prazoDeValidade ?? '');
  /* ⚠️ Três estados no campo: '' é "o documento não diz", que vira nulo. */
  const [substabelecer, setSubstabelecer] = useState<'' | 'sim' | 'nao'>(
    inicial?.podeSubstabelecer === true ? 'sim' : inicial?.podeSubstabelecer === false ? 'nao' : '',
  );
  const [sucessora, setSucessora] = useState(inicial?.matriculaSucessora ?? '');
  const [motivo, setMotivo] = useState(inicial?.motivoEncerramento ?? '');
  const [erro, setErro] = useState<string | null>(null);
  const [gravando, setGravando] = useState(false);

  /**
   * Vazio vira nulo, e nunca zero — "não quantificado".
   *
   * ⛔⛔ **O PONTO DE MILHAR PRECISA SAIR — corrigido em 10/09/2026.**
   *
   * Este helper trocava só a vírgula. `Number("1.200.000,00".replace(',','.'))`
   * é **NaN**, e NaN vira `null` no JSON: a avaliação que a pessoa digitou no
   * formato brasileiro **sumia sem erro nenhum na tela**. O formulário de ônus,
   * dez telas acima, já fazia certo — dois helpers com o mesmo nome fazendo
   * coisas diferentes, que é como uma casa passa a mentir em um lugar só.
   *
   * ⚠️ **Vale para as áreas também**, que entraram hoje: um terreno de
   * "1.250,50 m²" cairia na mesma armadilha.
   */
  const numero = (t: string) =>
    t.trim() === '' ? null : Number(t.replace(/\./g, '').replace(',', '.'));

  async function lancar(e: React.FormEvent) {
    e.preventDefault();
    if (descricao.trim().length < 2) {
      setErro('Descreva o bem — é o que sai impresso.');
      return;
    }
    /* ⚠️ A lista só vale inteira: linha sem valor ou sem procedência é recusada aqui, antes de gravar. */
    const pedeValor = PEDE_VALOR.includes(classificacao);
    const linhas = avaliacoes.filter((a) => a.valor.trim() !== '' || a.modalidade !== '' || a.referencia.trim() !== '');
    if (pedeValor && linhas.some((a) => numero(a.valor) === null || !(numero(a.valor)! > 0))) {
      setErro('Informe o valor de cada avaliação, ou tire a linha.');
      return;
    }
    if (pedeValor && linhas.some((a) => !a.id && a.modalidade === '')) {
      setErro('Diga de onde veio cada avaliação nova.');
      return;
    }
    setGravando(true);
    setErro(null);
    try {
      /*
       * ⚠️ **Corrigindo, manda os MESMOS campos** — e não só os que mudaram.
       * O servidor aceita parcial, mas mandar parcial daqui exigiria comparar
       * campo a campo com o que veio, e é aí que se esquece um. Mandar tudo é
       * o que garante que apagar um campo na tela apague no banco.
       */
      const dados = {
        classificacao,
        especie: categoria === 'veiculos' ? especie : null,
        descricao,
        identificacao: identificacao.trim() || null,
        /* ⭐ A lista manda; o servidor espelha a marcada em `avaliacao`. Fora
           das classificações com valor, a lista não vai — e não se mexe nela. */
        ...(pedeValor
          ? {
              avaliacoes: linhas.map((a) => ({
                ...(a.id ? { id: a.id } : {}),
                modalidade: a.modalidade || null,
                valor: numero(a.valor)!,
                referencia: a.referencia.trim() || null,
                avaliadaEm: a.avaliadaEm || null,
                entraNaConta: a.conta,
              })),
            }
          : {}),
        percentual: numero(percentual),
        valorDeclarado: numero(valorDeclarado),
        ocorridoEm: ocorridoEm || null,
        transmitente: transmitente.trim() || null,
        adquirente: adquirente.trim() || null,
        vinculoEntrePartes: vinculo.trim() || null,
        proprietarioAtual: proprietarioAtual.trim() || null,
        elementoDeConexao: conexao.trim() || null,
        tipoDoImovel: tipoDoImovel.trim() || null,
        cidade: cidade.trim() || null,
        uf: uf.trim() || null,
        /* ⚠️ **Campo em branco vira NULO, nunca zero.** Zero seria uma medida
           afirmada; nulo é "a matrícula não informou". */
        areaPrivativa: numero(areaPrivativa),
        areaDoTerreno: numero(areaDoTerreno),
        areaConstruida: numero(areaConstruida),
        modalidadeDeAquisicao: modalidadeDeAquisicao.trim() || null,
        certidaoEm: certidaoEm || null,
        tabelionato: tabelionato.trim() || null,
        papelNoAto: papelNoAto || null,
        efeitoPatrimonial: efeito || null,
        poderes: poderes.trim() || null,
        prazoDeValidade: prazo.trim() || null,
        podeSubstabelecer: substabelecer === '' ? null : substabelecer === 'sim',
        matriculaSucessora: sucessora.trim() || null,
        motivoEncerramento: motivo.trim() || null,
      };
      if (corrigindo) {
        await apiInvestigados.editarAtivo(investigadoId, corrigindo.id, dados);
        avisar('Lançamento corrigido.');
        aoLancar();
        aoCancelar?.();
        return;
      }
      const { ativo: criado } = await apiInvestigados.acrescentarAtivo(investigadoId, dados);
      setDescricao('');
      setIdentificacao('');
      setAvaliacoes([]);
      setPercentual('');
      setValorDeclarado('');
      setOcorridoEm('');
      setTransmitente('');
      setAdquirente('');
      setVinculo('');
      setProprietarioAtual('');
      setSucessora('');
      setMotivo('');
      /* ⛔ **Os campos novos limpam junto — 10/09/2026.** Deixar tipo, cidade e
         área de pé faria o segundo lançamento nascer com a descrição do
         primeiro, e ninguém notaria olhando a tela. */
      setConexao('');
      setTipoDoImovel('');
      setCidade('');
      setUf('');
      setAreaPrivativa('');
      setAreaDoTerreno('');
      setAreaConstruida('');
      setModalidadeDeAquisicao('');
      setCertidaoEm('');
      setTabelionato('');
      setPapelNoAto('');
      setEfeito('');
      setPoderes('');
      setPrazo('');
      setSubstabelecer('');
      aoLancar(criado);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui lançar.');
    } finally {
      setGravando(false);
    }
  }

  return (
    <form className={`form-ativo lr-form-ativo${corrigindo ? ' form-ativo-corrigindo' : ''}`} onSubmit={lancar}>
      {/* ⚠️ Corrigindo, a tela DIZ qual bem — sem isso, um formulário
          preenchido no meio da página parece um lançamento novo pela metade. */}
      {corrigindo && (
        <p className="lr-titulo">Corrigindo: {corrigindo.descricao.slice(0, 60)}</p>
      )}
      {/* ⭐ Linha rápida, 18/09/2026: o alto do formulário — o único pedaço que
          ainda aparece, nos processos — com os campos do desenho novo. O resto,
          das classificações que passaram ao passo a passo, fica como estava. */}
      <div className="imv-grade-campos">
        <CampoDoPasso rotulo="Classificação" col={categoria === 'veiculos' ? 2 : 3}>
          <select
            value={classificacao}
            onChange={(e) => setClassificacao(e.target.value as ClassificacaoDoAtivo)}
          >
            {opcoes.map((c) => (
              <option key={c} value={c}>
                {ROTULO[c]}
              </option>
            ))}
          </select>
        </CampoDoPasso>
        {categoria === 'veiculos' ? (
          <CampoDoPasso rotulo="Espécie" col={2}>
            <select value={especie} onChange={(e) => setEspecie(e.target.value as EspecieDeVeiculo)}>
              {ESPECIES.map((e) => (
                <option key={e} value={e}>
                  {ESPECIE[e]}
                </option>
              ))}
            </select>
          </CampoDoPasso>
        ) : null}
        <CampoDoPasso rotulo="Identificação" col={categoria === 'veiculos' ? 2 : 3} mono>
          <input
            value={identificacao}
            onChange={(e) => setIdentificacao(e.target.value)}
            placeholder="Matrícula 57.470"
          />
        </CampoDoPasso>
        {/*
          ⚠️ **O que sai impresso nomeia o REGISTRO, nunca a ferramenta.** "2º
          Registro de Imóveis de Sorocaba/SP" pode; "localizado no painel da
          Assertiva" não. É a regra de sigilo do método, e é aqui que a tentação
          aparece.
        */}
        <CampoDoPasso
          rotulo="O bem"
          obrigatorio
          col={6}
          nota="Nomeie o registro que detém o fato — o cartório, a vara, o órgão. Nunca a plataforma consultada."
        >
          <input
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            placeholder={
              categoria === 'veiculos'
                ? especie === 'aeronave'
                  ? 'PP-ABC — Cessna 680 Citation Sovereign'
                  : especie === 'embarcacao'
                    ? 'Lancha Ficta — inscrição 1234567890, Capitania de Santos/SP'
                    : PISTA[categoria]
                : PISTA[categoria]
            }
          />
        </CampoDoPasso>
      </div>

      {PEDE_VALOR.includes(classificacao) && (
        <div className="dupla">
          <label className="campo-porcento">
            <span className="rotulo">Participação (opcional)</span>
            <input
              inputMode="decimal"
              value={percentual}
              onChange={(e) => setPercentual(e.target.value)}
              placeholder="25"
            />
            <span className="ajuda">Em %. Em branco não vira 100%.</span>
          </label>
          <label className="campo-data">
            <span className="rotulo">Data do fato (opcional)</span>
            <input type="date" value={ocorridoEm} onChange={(e) => setOcorridoEm(e.target.value)} />
          </label>
        </div>
      )}

      {/*
        ⭐⭐ **O VALOR DECLARADO NO ATO passou a aparecer SEMPRE — 10/09/2026.**

        ⛔ **Ele só existia dentro da transmissão**, e por isso era impreenchível
        justamente nos dois casos mais comuns: imóvel livre e imóvel onerado. A
        coluna "valor de aquisição" da tabela que o Richard mandou era, na
        prática, inalcançável pela tela.

        ⚠️ **E ele NÃO é a avaliação, nunca** — regra dele: *"o valor declarado
        não sendo tratado automaticamente como valor de mercado"*. É a distância
        entre os dois que interessa, e por isso os dois campos coexistem.
      */}
      {PEDE_VALOR.includes(classificacao) && !PEDE_TRANSMISSAO.includes(classificacao) && (
        <label className="campo campo-dinheiro">
          <span className="rotulo">Valor declarado no ato (opcional)</span>
          <input
            inputMode="decimal"
            value={valorDeclarado}
            onChange={(e) => setValorDeclarado(digitarReais(e.target.value))}
            onBlur={(e) => setValorDeclarado(completarReais(e.target.value))}
            placeholder="376.800,00"
          />
          <span className="ajuda">
            O preço que consta do registro. <strong>Não é a avaliação</strong> — é a distância
            entre os dois que interessa.
          </span>
        </label>
      )}

      {PEDE_TRANSMISSAO.includes(classificacao) && (
        <>
          <div className="tripla">
            <label>
              <span className="rotulo">Transmitente (opcional)</span>
              <input value={transmitente} onChange={(e) => setTransmitente(e.target.value)} />
            </label>
            <label>
              <span className="rotulo">Adquirente (opcional)</span>
              <input value={adquirente} onChange={(e) => setAdquirente(e.target.value)} />
            </label>
            <label>
              <span className="rotulo">Vínculo entre as partes (opcional)</span>
              <input
                value={vinculo}
                onChange={(e) => setVinculo(e.target.value)}
                placeholder="Irmão do investigado"
              />
            </label>
          </div>
          <div className="dupla">
            <label>
              <span className="rotulo">Valor declarado no ato (opcional)</span>
              <input
                inputMode="decimal"
                value={valorDeclarado}
                onChange={(e) => setValorDeclarado(digitarReais(e.target.value))}
                onBlur={(e) => setValorDeclarado(completarReais(e.target.value))}
              />
              <span className="ajuda">
                Sai separado da avaliação — declarado não é valor de mercado.
              </span>
            </label>
            <label>
              <span className="rotulo">Proprietário atual (opcional)</span>
              <input
                value={proprietarioAtual}
                onChange={(e) => setProprietarioAtual(e.target.value)}
              />
            </label>
          </div>
        </>
      )}

      {/*
        ⭐ **O que descreve o imóvel — 10/09/2026.** Sem tipo, comarca e área,
        uma lista de matrículas não orienta ninguém: comarca decide onde se
        penhora, e área é o que sustenta qualquer estimativa de valor.
      */}
      {PEDE_DESCRICAO_DO_IMOVEL.includes(classificacao) && (
        <>
          <div className="tripla">
            <label>
              <span className="rotulo">Tipo do imóvel (opcional)</span>
              <input
                value={tipoDoImovel}
                onChange={(e) => setTipoDoImovel(e.target.value)}
                placeholder="Apartamento · casa · terreno · galpão · rural"
              />
            </label>
            <label>
              <span className="rotulo">Cidade (opcional)</span>
              <input value={cidade} onChange={(e) => setCidade(e.target.value)} />
            </label>
            <label className="campo-uf">
              <span className="rotulo">UF (opcional)</span>
              <input
                value={uf}
                maxLength={2}
                onChange={(e) => setUf(e.target.value.toUpperCase())}
              />
            </label>
          </div>
          <div className="tripla">
            <label>
              <span className="rotulo">Área privativa, m² (opcional)</span>
              <input
                inputMode="decimal"
                value={areaPrivativa}
                onChange={(e) => setAreaPrivativa(e.target.value)}
              />
            </label>
            <label>
              <span className="rotulo">Área do terreno, m² (opcional)</span>
              <input
                inputMode="decimal"
                value={areaDoTerreno}
                onChange={(e) => setAreaDoTerreno(e.target.value)}
              />
            </label>
            <label>
              <span className="rotulo">Área construída, m² (opcional)</span>
              <input
                inputMode="decimal"
                value={areaConstruida}
                onChange={(e) => setAreaConstruida(e.target.value)}
              />
            </label>
          </div>
          {/*
            ⚠️ **As três, e não "a que representa melhor".** Guardadas as três,
            quem monta a tabela escolhe qual mostrar — e nenhuma se perde.
          */}
          <p className="ajuda ajuda-solta">
            Preencha as que a matrícula informar. <strong>Campo em branco fica em branco</strong> —
            nunca vira zero, que seria uma medida afirmada.
          </p>
          <div className="dupla">
            {/*
              ⭐⭐ **A SUGESTÃO DE TIPOS DE ATO — 21/09/2026.** O campo continua
              de escrita livre (lista fechada obrigaria a traduzir o que o
              registro diz), e a lista existe porque **o Hub LÊ o que se escreve
              aqui**: os sinais de blindagem procuram doação, integralização,
              partilha e usufruto no texto do lançamento, e "doação c/ usufr."
              faria o padrão passar despercebido.

              ⭐ Os quatro que levam patrimônio à família vêm primeiro —
              alfabética os esconderia entre os corriqueiros.
            */}
            <label>
              <span className="rotulo">Modalidade de aquisição (opcional)</span>
              <input
                value={modalidadeDeAquisicao}
                onChange={(e) => setModalidadeDeAquisicao(e.target.value)}
                placeholder="Compra e venda · doação · partilha · arrematação"
                list="tipos-de-ato"
              />
              <datalist id="tipos-de-ato">
                {TIPOS_DE_ATO.map((x) => (
                  <option key={x} value={x} />
                ))}
              </datalist>
              <small className="ajuda">
                Escrita livre. A lista traz os atos que o Hub reconhece, começando pelos que levam
                patrimônio à família.
              </small>
            </label>
            {/*
              ⭐⭐ **A DATA DA CERTIDÃO — 10/09/2026, e não é a da aquisição.**
              Uma matrícula de 2019 pode ter recebido penhora em 2024; o
              relatório que a apresenta como situação atual afirma o que não sabe.
            */}
            <label>
              <span className="rotulo">Data da certidão (opcional)</span>
              <input
                type="date"
                value={certidaoEm}
                onChange={(e) => setCertidaoEm(e.target.value)}
              />
              <span className="ajuda">
                De quando é o <strong>retrato registral</strong> — não é a data da aquisição.
                Passados <strong>90 dias</strong> o Hub avisa que a situação pode ter mudado.
              </span>
            </label>
          </div>
        </>
      )}

      {/*
        ⭐⭐ **AS AVALIAÇÕES DO BEM — várias, decisão dele, 15/09/2026.** Venal,
        terra nua, mercadológica, comparativo, judicial: cada uma com de onde
        veio, e uma marcada como a que entra na conta do patrimônio. ⚠️ A
        procedência continua sendo o que impede um chute de parecer laudo —
        *"R$ 800.000 (avaliação judicial)"* e *"R$ 800.000 (preço médio por m²)"*
        são o mesmo número com pesos opostos.
      */}
      {PEDE_VALOR.includes(classificacao) && (
        <fieldset className="avaliacoes-do-bem">
          <legend className="rotulo">Avaliações do bem (opcional)</legend>
          {avaliacoes.length === 0 && (
            <p className="ajuda">
              Nenhuma avaliação. Sem avaliação o bem conta no patrimônio sem somar dinheiro — não é zero.
            </p>
          )}
          {avaliacoes.map((a, i) => {
            const mudar = (parte: Partial<LinhaDeAvaliacao>) =>
              setAvaliacoes((todas) => todas.map((x, k) => (k === i ? { ...x, ...parte } : x)));
            return (
              <div key={a.id ?? `nova-${i}`} className="avaliacao-linha">
                <select
                  aria-label="De onde veio a avaliação"
                  value={a.modalidade}
                  onChange={(e) => mudar({ modalidade: e.target.value as ModalidadeDaAvaliacao | '' })}
                >
                  <option value="">De onde veio…</option>
                  {MODALIDADES.map((m) => (
                    <option key={m.chave} value={m.chave}>
                      {m.rotulo}
                      {m.documentada ? '' : ' (derivada)'}
                    </option>
                  ))}
                </select>
                <input
                  aria-label="Valor da avaliação"
                  className="avaliacao-valor"
                  inputMode="decimal"
                  value={a.valor}
                  onChange={(e) => mudar({ valor: digitarReais(e.target.value) })}
                  onBlur={(e) => mudar({ valor: completarReais(e.target.value) })}
                  placeholder="250.000,00"
                />
                <input
                  aria-label="Referência da avaliação"
                  value={a.referencia}
                  onChange={(e) => mudar({ referencia: e.target.value })}
                  placeholder="R.2 da matrícula, IPTU de 2026…"
                />
                <input
                  aria-label="Data da avaliação"
                  type="date"
                  value={a.avaliadaEm}
                  onChange={(e) => mudar({ avaliadaEm: e.target.value })}
                />
                <label className="avaliacao-conta">
                  <input
                    type="checkbox"
                    checked={a.conta}
                    onChange={(e) =>
                      setAvaliacoes((todas) => todas.map((x, k) => ({ ...x, conta: k === i ? e.target.checked : false })))
                    }
                  />
                  entra na conta
                </label>
                <button
                  type="button"
                  className="botao-texto"
                  onClick={() => setAvaliacoes((todas) => todas.filter((_, k) => k !== i))}
                >
                  tirar
                </button>
              </div>
            );
          })}
          <button
            type="button"
            className="botao-secundario"
            onClick={() =>
              setAvaliacoes((todas) => [
                ...todas,
                { modalidade: '', valor: '', referencia: '', avaliadaEm: '', conta: todas.every((x) => !x.conta) },
              ])
            }
          >
            + Acrescentar avaliação
          </button>
          <span className="ajuda">
            Do bem integral. Marque a que <strong>entra na conta</strong> do patrimônio. Judicial, laudo e
            valor declarado avaliam <strong>este</strong> bem; as demais são <strong>derivadas</strong> —
            continuam válidas, e o relatório dirá que são. <strong>A apurar</strong> não é zero.
          </span>
        </fieldset>
      )}

      {/*
        ⭐⭐ **ESCRITURAS E PROCURAÇÕES — 10/09/2026.**

        ⚠️ **Os rótulos mudam com a espécie, e as colunas do banco não.**
        `transmitente` é o transmitente numa escritura e o **outorgante** numa
        procuração; `adquirente` é o adquirente e o **procurador**. Trocar o
        nome da coluna seria migração em cinco lugares para mudar palavra que
        só o olho lê — mesma decisão de `grupo_economico` em 20/08.

        ⛔ **Poder amplo NÃO é ato praticado**, e a ajuda diz isso: uma
        procuração para vender não vendeu nada, e o relatório que confunde os
        dois afirma transmissão que não houve.
      */}
      {PEDE_ATO_NOTARIAL.includes(classificacao) && (
        <>
          <div className="dupla">
            <label>
              <span className="rotulo">Tabelionato (opcional)</span>
              <input
                value={tabelionato}
                onChange={(e) => setTabelionato(e.target.value)}
                placeholder="2º Tabelião de Notas de Santos/SP"
              />
              <span className="ajuda">
                Pode sair no documento entregue: é o <strong>registro oficial</strong> que
                detém o fato, como a Receita e a junta.
              </span>
            </label>
            <label>
              <span className="rotulo">
                {classificacao === 'procuracao'
                  ? 'O investigado é (opcional)'
                  : 'Qualidade do investigado no ato (opcional)'}
              </span>
              <select
                value={papelNoAto}
                onChange={(e) => setPapelNoAto(e.target.value as PapelNoAtoNotarial | '')}
              >
                <option value="">—</option>
                {PAPEIS.filter((x) =>
                  classificacao === 'procuracao' ? x.deProcuracao : !x.deProcuracao,
                ).map((x) => (
                  <option key={x.chave} value={x.chave}>
                    {x.rotulo}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {classificacao === 'escritura' && (
            <label>
              <span className="rotulo">O que o ato fez com o patrimônio (opcional)</span>
              <select
                value={efeito}
                onChange={(e) => setEfeito(e.target.value as EfeitoPatrimonialDoAto | '')}
              >
                <option value="">—</option>
                <OpcoesAgrupadas itens={EFEITOS_DO_ATO} atual={efeito} sufixo={sufixoDoEfeito} />
              </select>
              <span className="ajuda">
                ⚠️ Não é a classificação do lançamento: aquela diz o que ele <strong>é</strong>,
                esta diz o que ele <strong>fez</strong>. "Sem efeito identificado" é resposta
                legítima e melhor que forçar um efeito que o ato não teve.
              </span>
            </label>
          )}

          {classificacao === 'procuracao' && (
            <>
              <label>
                <span className="rotulo">Poderes patrimonialmente relevantes (opcional)</span>
                <textarea
                  rows={3}
                  value={poderes}
                  onChange={(e) => setPoderes(e.target.value)}
                  placeholder="Comprar e vender imóveis; movimentar contas bancárias; dar quitação."
                />
                <span className="ajuda">
                  <strong>Resuma, não transcreva.</strong> ⛔ E lembre: poder para vender{' '}
                  <strong>não é venda feita</strong> — a procuração mostra o que se pode fazer,
                  não o que se fez.
                </span>
              </label>
              <div className="dupla">
                <label>
                  <span className="rotulo">Prazo ou validade (opcional)</span>
                  <input
                    value={prazo}
                    onChange={(e) => setPrazo(e.target.value)}
                    placeholder="Indeterminado · até 31/12/2027 · 90 dias"
                  />
                </label>
                <label>
                  <span className="rotulo">Permite substabelecer? (opcional)</span>
                  <select
                    value={substabelecer}
                    onChange={(e) => setSubstabelecer(e.target.value as '' | 'sim' | 'nao')}
                  >
                    <option value="">o documento não diz</option>
                    <option value="sim">sim</option>
                    <option value="nao">não</option>
                  </select>
                  <span className="ajuda">
                    ⚠️ Poder para substabelecer <strong>não significa</strong> que houve
                    substabelecimento.
                  </span>
                </label>
              </div>
            </>
          )}
        </>
      )}

      {PEDE_CONEXAO.includes(classificacao) && (
        <>
          <div className="dupla">
            <label>
              <span className="rotulo">Proprietário registral</span>
              <input
                value={proprietarioAtual}
                onChange={(e) => setProprietarioAtual(e.target.value)}
                placeholder="Quem consta na matrícula hoje"
              />
            </label>
            <label>
              <span className="rotulo">Relação com o investigado</span>
              <input
                value={vinculo}
                onChange={(e) => setVinculo(e.target.value)}
                placeholder="Cônjuge · filho · sócio · empresa do grupo"
              />
            </label>
          </div>
          <label>
            <span className="rotulo">Elemento de conexão</span>
            <input
              value={conexao}
              onChange={(e) => setConexao(e.target.value)}
              placeholder="O endereço declarado pelo investigado é o deste imóvel"
            />
            {/*
              ⚠️ **A ajuda diz o que NÃO escrever, e é de propósito.** O campo
              convida a resumir a suspeita — "é laranja do devedor" —, e é
              exatamente isso que o documento não pode carregar.
            */}
            <span className="ajuda">
              <strong>Obrigatório.</strong> O fato objetivo que justifica olhar este imóvel —
              não a conclusão sobre ele. "Reside no endereço que o investigado declarou"
              entra; "é laranja do devedor" não. ⚠️ Este imóvel <strong>não soma</strong> ao
              patrimônio do investigado: o lançamento demonstra o vínculo, não a propriedade.
            </span>
          </label>
        </>
      )}

      {PEDE_ENCERRAMENTO.includes(classificacao) && (
        <div className="dupla">
          <label className="campo-identificacao">
            <span className="rotulo">Matrícula sucessora (opcional)</span>
            <input value={sucessora} onChange={(e) => setSucessora(e.target.value)} />
          </label>
          <label>
            <span className="rotulo">Motivo do encerramento (opcional)</span>
            <input
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Unificação, desmembramento, loteamento"
            />
          </label>
        </div>
      )}

      {erro && <p className="erro">{erro}</p>}

      <div className="lr-acoes">
        {/* ⚠️ Sair da correção precisa ser possível sem gravar — senão a única
            saída é recarregar a página, e aí o que já foi digitado se perde. */}
        {corrigindo && (
          <button type="button" className="imv-bt fantasma" onClick={aoCancelar}>
            Cancelar
          </button>
        )}
        <button type="submit" className="imv-bt primario" disabled={gravando}>
          <Icone nome={corrigindo ? 'check' : 'mais'} tamanho={14} />
          {gravando ? (corrigindo ? 'Gravando…' : 'Lançando…') : corrigindo ? 'Gravar a correção' : 'Lançar'}
        </button>
      </div>
    </form>
  );
}

/* ═══ A FICHA PASSO A PASSO DE VEÍCULO E DE ATO NOTARIAL — 18/09/2026 ═════════
 *
 * O quinto da fila (`docs/FORMULARIOS-E-LINK-DO-CLIENTE.md`, A.4), no desenho do
 * tópico Imóveis — a ficha abre no lugar da lista e volta a ela ao gravar — e com
 * as peças comuns (`PassoAPasso.tsx`).
 *
 * ⛔ **Muda o desenho, não o que o botão faz.** Os mesmos campos da janela antiga
 * (`Formulario`, que continua servindo aos processos), pela mesma rota e com o
 * mesmo corpo — inclusive os campos que a ficha não mostra, que vão como vieram,
 * para a correção não apagar nada.
 *
 * O que mudou além do desenho, dito a ele:
 * - **O ato ganhou a data** — a coluna existia e a janela antiga não a mostrava
 *   para escritura e procuração;
 * - **O veículo lança os gravames junto**, como o imóvel;
 * - **Gravar pergunta, e fechar com o que foi digitado avisa** — a regra de todo
 *   formulário do Hub (`lib/formulario.ts`), que a janela antiga não seguia.
 */

type CategoriaDaFicha = 'veiculos' | 'atos';
type GravameNaFicha = { natureza: string; credor: string; valor: string; constituidoEm: string; situacao: string };

/** A situação do veículo, com o que ela faz na conta — o tom é o da classificação do imóvel. */
const SITUACAO_DO_VEICULO: Array<{ chave: ClassificacaoDoAtivo; curto: string; tom: string; define: string }> = [
  { chave: 'veiculo_atual', curto: 'Atual', tom: 'livre', define: 'No nome do investigado hoje. Soma no patrimônio atual pela avaliação marcada.' },
  { chave: 'veiculo_historico', curto: 'Histórico', tom: 'transmitido', define: 'Esteve no nome dele e saiu. É patrimônio pretérito: aparece separado e não soma.' },
  { chave: 'veiculo_baixado', curto: 'Baixado ou inativo', tom: 'encerrada', define: 'Baixa, sucateamento ou perda total. Não soma — a avaliação ali é só referência.' },
];
const ESPECIE_NO_SINGULAR: Record<EspecieDeVeiculo, string> = { automovel: 'Automóvel', aeronave: 'Aeronave', embarcacao: 'Embarcação' };
const IDENTIFICACAO_DA_ESPECIE: Record<EspecieDeVeiculo, string> = {
  automovel: 'RENAVAM 00000000000',
  aeronave: 'Matrícula PP-ABC',
  embarcacao: 'Inscrição 1234567890',
};
const PISTA_DA_ESPECIE: Record<EspecieDeVeiculo, string> = {
  automovel: PISTA.veiculos,
  aeronave: 'PP-ABC — Cessna 680 Citation Sovereign',
  embarcacao: 'Lancha Ficta — inscrição 1234567890, Capitania de Santos/SP',
};
const ESPECIE_DO_ATO: Array<{ chave: ClassificacaoDoAtivo; curto: string; define: string }> = [
  { chave: 'escritura', curto: 'Escritura pública', define: 'Evidência de movimentação, não bem: não soma no patrimônio atual até a titularidade vigente se confirmar.' },
  { chave: 'procuracao', curto: 'Procuração pública', define: 'Não transfere propriedade e não é patrimônio por si: mostra o que se pode fazer, não o que se fez.' },
];
const NATUREZAS_DO_VEICULO = ['Alienação fiduciária', 'Arrendamento mercantil', 'Reserva de domínio', 'Penhora', 'Arresto', 'Restrição judicial', 'Indisponibilidade'];

export function FichaDoAtivo({
  investigado,
  categoria,
  corrigindo,
  aoFechar,
  aoGravar,
}: {
  investigado: Investigado;
  categoria: CategoriaDaFicha;
  corrigindo: AtivoDoInvestigado | null;
  aoFechar: () => void;
  aoGravar: () => void;
}) {
  const ehMaster = usarEhMaster();
  const veiculo = categoria === 'veiculos';
  const inicial = corrigindo;
  const emCampo = (v: number | null | undefined) => (v === null || v === undefined ? '' : String(v).replace('.', ','));
  const emReais = (v: number | null | undefined) =>
    v === null || v === undefined ? '' : v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const [classificacao, setClassificacao] = useState<ClassificacaoDoAtivo>(inicial?.classificacao ?? CLASSIFICACOES[categoria][0]!);
  const [especie, setEspecie] = useState<EspecieDeVeiculo>(inicial?.especie ?? 'automovel');
  const [descricao, setDescricao] = useState(inicial?.descricao ?? '');
  const [identificacao, setIdentificacao] = useState(inicial?.identificacao ?? '');
  const [avaliacoes, setAvaliacoes] = useState<LinhaDeAvaliacao[]>(() => {
    const lista = inicial?.avaliacoes ?? [];
    if (lista.length > 0) {
      return lista.map((a) => ({ id: a.id, modalidade: a.modalidade ?? '', valor: emReais(a.valor), referencia: a.referencia ?? '', avaliadaEm: a.avaliadaEm ?? '', conta: a.entraNaConta }));
    }
    if (inicial?.avaliacao !== null && inicial?.avaliacao !== undefined) {
      return [{ modalidade: inicial.modalidadeDaAvaliacao ?? '', valor: emReais(inicial.avaliacao), referencia: '', avaliadaEm: '', conta: true }];
    }
    return [];
  });
  const [percentual, setPercentual] = useState(emCampo(inicial?.percentual));
  const [valorDeclarado, setValorDeclarado] = useState(emReais(inicial?.valorDeclarado));
  const [ocorridoEm, setOcorridoEm] = useState(inicial?.ocorridoEm ?? '');
  const [transmitente, setTransmitente] = useState(inicial?.transmitente ?? '');
  const [adquirente, setAdquirente] = useState(inicial?.adquirente ?? '');
  const [vinculo, setVinculo] = useState(inicial?.vinculoEntrePartes ?? '');
  const [proprietarioAtual, setProprietarioAtual] = useState(inicial?.proprietarioAtual ?? '');
  const [tabelionato, setTabelionato] = useState(inicial?.tabelionato ?? '');
  const [papelNoAto, setPapelNoAto] = useState<PapelNoAtoNotarial | ''>(inicial?.papelNoAto ?? '');
  const [efeito, setEfeito] = useState<EfeitoPatrimonialDoAto | ''>(inicial?.efeitoPatrimonial ?? '');
  const [poderes, setPoderes] = useState(inicial?.poderes ?? '');
  const [prazo, setPrazo] = useState(inicial?.prazoDeValidade ?? '');
  const [substabelecer, setSubstabelecer] = useState<'' | 'sim' | 'nao'>(
    inicial?.podeSubstabelecer === true ? 'sim' : inicial?.podeSubstabelecer === false ? 'nao' : '',
  );
  const [gravames, setGravames] = useState<GravameNaFicha[]>([]);
  /* Os gravames de um bem que já existe vêm do servidor e mudam na hora. */
  const [onusExistentes, setOnusExistentes] = useState(corrigindo?.onus ?? []);
  const [gravameAApagar, setGravameAApagar] = useState<AtivoDoInvestigado['onus'][number] | null>(null);

  const escritura = classificacao === 'escritura';
  const procuracao = classificacao === 'procuracao';
  const historico = PEDE_TRANSMISSAO.includes(classificacao);
  const pedeValor = PEDE_VALOR.includes(classificacao);
  const passos = veiculo
    ? ['O veículo', 'Aquisição ou saída', 'Avaliações', 'Gravames', corrigindo ? 'Conferir e gravar' : 'Conferir e lançar']
    : ['O ato', 'Quem participou', procuracao ? 'Os poderes' : 'O negócio', corrigindo ? 'Conferir e gravar' : 'Conferir e lançar'];
  const ULTIMO = passos.length - 1;
  const P_AVALIACOES = veiculo ? 2 : -1;
  const [passo, setPasso] = useState(0);
  const [alcancado, setAlcancado] = useState(corrigindo ? ULTIMO : 0);
  const [erro, setErro] = useState<string | null>(null);
  const [gravando, setGravando] = useState(false);
  const emVoo = useRef(false);
  const topo = useRef<HTMLDivElement>(null);

  /* ⚠️ Tudo o que a ficha edita entra na guarda: fechar com algo digitado avisa. */
  const guarda = usarFormulario(
    { classificacao, especie, descricao, identificacao, avaliacoes, percentual, valorDeclarado, ocorridoEm, transmitente, adquirente, vinculo, proprietarioAtual, tabelionato, papelNoAto, efeito, poderes, prazo, substabelecer, gravames },
    aoFechar,
  );
  useEffect(() => {
    topo.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [passo]);

  /** Vazio vira nulo, e nunca zero. O ponto de milhar sai antes (a lição de 10/09). */
  const numero = (t: string) => (t.trim() === '' ? null : Number(t.replace(/\./g, '').replace(',', '.')));
  const linhasDeAvaliacao = avaliacoes.filter((a) => a.valor.trim() !== '' || a.modalidade !== '' || a.referencia.trim() !== '');

  /* O aviso some assim que o passo deixa de ter o problema — como em imóveis. */
  useEffect(() => {
    if (erro && !problemaDoPasso(passo)) setErro(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [descricao, avaliacoes, classificacao]);

  function problemaDoPasso(p: number): string | null {
    if (p === 0 && descricao.trim().length < 2) return veiculo ? 'Descreva o veículo — é o que sai impresso.' : 'Descreva o ato — é o que sai impresso.';
    if (p === P_AVALIACOES && pedeValor) {
      if (linhasDeAvaliacao.some((a) => numero(a.valor) === null || !(numero(a.valor)! > 0))) return 'Informe o valor de cada avaliação, ou tire a linha.';
      if (linhasDeAvaliacao.some((a) => !a.id && a.modalidade === '')) return 'Diga de onde veio cada avaliação nova.';
    }
    return null;
  }
  function irPara(p: number) {
    setErro(null);
    setPasso(p);
  }
  function avancar() {
    const x = problemaDoPasso(passo);
    if (x) return setErro(x);
    setAlcancado((a) => Math.max(a, passo + 1));
    irPara(passo + 1);
  }
  function pedirParaGravar() {
    if (emVoo.current) return;
    for (let k = 0; k < ULTIMO; k++) {
      const x = problemaDoPasso(k);
      if (x) {
        setPasso(k);
        return setErro(x);
      }
    }
    guarda.pedirParaSalvar();
  }

  async function gravar() {
    if (emVoo.current) return;
    guarda.cancelarPendencia();
    emVoo.current = true;
    setGravando(true);
    setErro(null);
    /*
     * ⚠️ **O MESMO corpo da janela antiga**, campo a campo — inclusive os que a
     * ficha não mostra, que vão como vieram. Mandar só os editados deixaria a
     * correção dependente de lembrar cada um.
     */
    const dados = {
      classificacao,
      especie: veiculo ? especie : null,
      descricao,
      identificacao: identificacao.trim() || null,
      ...(pedeValor
        ? {
            avaliacoes: linhasDeAvaliacao.map((a) => ({
              ...(a.id ? { id: a.id } : {}),
              modalidade: a.modalidade || null,
              valor: numero(a.valor)!,
              referencia: a.referencia.trim() || null,
              avaliadaEm: a.avaliadaEm || null,
              entraNaConta: a.conta,
            })),
          }
        : {}),
      percentual: numero(percentual),
      valorDeclarado: numero(valorDeclarado),
      ocorridoEm: ocorridoEm || null,
      transmitente: transmitente.trim() || null,
      adquirente: adquirente.trim() || null,
      vinculoEntrePartes: vinculo.trim() || null,
      proprietarioAtual: proprietarioAtual.trim() || null,
      elementoDeConexao: inicial?.elementoDeConexao ?? null,
      tipoDoImovel: inicial?.tipoDoImovel ?? null,
      cidade: inicial?.cidade ?? null,
      uf: inicial?.uf ?? null,
      areaPrivativa: inicial?.areaPrivativa ?? null,
      areaDoTerreno: inicial?.areaDoTerreno ?? null,
      areaConstruida: inicial?.areaConstruida ?? null,
      modalidadeDeAquisicao: inicial?.modalidadeDeAquisicao ?? null,
      certidaoEm: inicial?.certidaoEm ?? null,
      tabelionato: tabelionato.trim() || null,
      papelNoAto: papelNoAto || null,
      efeitoPatrimonial: efeito || null,
      poderes: poderes.trim() || null,
      prazoDeValidade: prazo.trim() || null,
      podeSubstabelecer: substabelecer === '' ? null : substabelecer === 'sim',
      matriculaSucessora: inicial?.matriculaSucessora ?? null,
      motivoEncerramento: inicial?.motivoEncerramento ?? null,
    };
    try {
      if (corrigindo) {
        await apiInvestigados.editarAtivo(investigado.id, corrigindo.id, dados);
        avisar('Lançamento corrigido.');
        aoGravar();
        return;
      }
      const { ativo: criado } = await apiInvestigados.acrescentarAtivo(investigado.id, dados);
      /* ⭐ Os gravames seguem junto, como no imóvel — a falha de um não derruba o lançamento. */
      const aLancar = veiculo ? gravames.filter((g) => g.natureza.trim().length >= 2) : [];
      let falharam = 0;
      for (const g of aLancar) {
        try {
          await apiInvestigados.acrescentarOnus(investigado.id, criado.id, {
            natureza: g.natureza.trim(),
            credor: g.credor.trim() || null,
            valor: numero(g.valor),
            constituidoEm: g.constituidoEm || null,
            situacao: g.situacao || null,
          });
        } catch {
          falharam += 1;
        }
      }
      const oQue = veiculo ? 'Veículo lançado' : `${escritura ? 'Escritura' : 'Procuração'} lançada`;
      avisar(
        aLancar.length === 0
          ? `${oQue}.`
          : falharam === 0
            ? `${oQue}, com ${aLancar.length} gravame(s).`
            : `${oQue}; ${falharam} gravame(s) falharam e precisam ser lançados à mão.`,
      );
      aoGravar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui gravar.');
      emVoo.current = false;
      setGravando(false);
    }
  }

  const papeis = PAPEIS.filter((x) => (procuracao ? x.deProcuracao : !x.deProcuracao));
  const situacao = SITUACAO_DO_VEICULO.find((s) => s.chave === classificacao);
  const especieDoAto = ESPECIE_DO_ATO.find((s) => s.chave === classificacao);
  const marcada = linhasDeAvaliacao.find((a) => a.conta);
  const ajuda = passo < ULTIMO ? (veiculo ? AJUDA_DO_VEICULO : AJUDA_DO_ATO)[passo] : undefined;
  /* ⚠️ Nos passos de tabela a ajuda vai numa faixa acima: a coluna do relatório é estreita, e a tabela precisa da largura inteira. */
  const ajudaEmFaixa = veiculo && (passo === 2 || passo === 3);
  const nomeDaFicha = veiculo ? 'veículo' : 'ato notarial';

  return (
    <div className="imv pap-ficha va-ficha" ref={topo}>
      <section className="imv-ficha" aria-label={`${corrigindo ? 'Corrigir' : 'Lançar'} ${nomeDaFicha}`}>
        <header className="imv-ficha-topo">
          <div>
            <span className="s-sobrelinha">
              {corrigindo ? 'Corrigindo' : veiculo ? 'Novo veículo' : 'Novo ato notarial'} · passo {passo + 1} de {passos.length}
            </span>
            <h3>
              {corrigindo ? `Corrigir ${nomeDaFicha} de ` : `Lançar ${nomeDaFicha} de `}
              {investigado.nome}
            </h3>
            <p>Um assunto por vez. Volte a qualquer passo sem perder o que digitou.</p>
          </div>
          <button type="button" className="imv-fechar" onClick={guarda.fechar} aria-label="Fechar sem gravar">
            <Icone nome="fechar" tamanho={16} />
          </button>
        </header>
        <PassosDoTopo passos={passos} atual={passo} alcancado={alcancado} aoIr={irPara} />

        <div className="imv-corpo">
          <div className="imv-conteudo">
            {ajuda && ajudaEmFaixa && (
              <AjudaDoPasso titulo={ajuda.titulo} faixa>
                {ajuda.corpo(classificacao)}
              </AjudaDoPasso>
            )}
            {/* ── VEÍCULO ─────────────────────────────────────────────── */}
            {veiculo && passo === 0 && (
              <>
                <span className="imv-rotulo">Situação</span>
                <div className="imv-seg va-seg" role="radiogroup" aria-label="Situação do veículo">
                  {SITUACAO_DO_VEICULO.map((s) => (
                    <button key={s.chave} type="button" role="radio" aria-checked={classificacao === s.chave} className={`imv-op ${s.tom}${classificacao === s.chave ? ' on' : ''}`} onClick={() => setClassificacao(s.chave)}>
                      <i />
                      {s.curto}
                    </button>
                  ))}
                </div>
                {situacao && <p className={`imv-def ${situacao.tom}`}>{situacao.define}</p>}
                <span className="imv-rotulo">Espécie</span>
                <div className="imv-seg va-seg" role="radiogroup" aria-label="Espécie do veículo">
                  {ESPECIES.map((e) => (
                    <button key={e} type="button" role="radio" aria-checked={especie === e} className={`imv-op neutro${especie === e ? ' on' : ''}`} onClick={() => setEspecie(e)}>
                      <i />
                      {ESPECIE_NO_SINGULAR[e]}
                    </button>
                  ))}
                </div>
                <div className="imv-grade-campos va-campos">
                  <CampoDoPasso rotulo="O veículo" obrigatorio col={6} nota="Nomeie o registro que detém o fato — o órgão, a capitania. Nunca a plataforma consultada.">
                    <input id="va-descricao" value={descricao} onChange={(e) => setDescricao(e.target.value)} placeholder={PISTA_DA_ESPECIE[especie]} autoFocus={!corrigindo} />
                  </CampoDoPasso>
                  <CampoDoPasso rotulo="Identificação" col={3} mono>
                    <input id="va-identificacao" value={identificacao} onChange={(e) => setIdentificacao(e.target.value)} placeholder={IDENTIFICACAO_DA_ESPECIE[especie]} />
                  </CampoDoPasso>
                </div>
              </>
            )}

            {veiculo && passo === 1 && (
              <div className="imv-grade-campos">
                <CampoDoPasso rotulo={historico ? 'Data da saída' : 'Data da aquisição'} col={2} mono>
                  <input id="va-data" type="date" value={ocorridoEm} onChange={(e) => setOcorridoEm(e.target.value)} />
                </CampoDoPasso>
                <CampoDoPasso rotulo="Participação" col={2} mono nota="Em branco não vira 100%.">
                  <input id="va-percentual" inputMode="decimal" value={percentual} onChange={(e) => setPercentual(e.target.value)} placeholder="—" />
                  <i className="imv-suf">%</i>
                </CampoDoPasso>
                <CampoDoPasso rotulo="Valor declarado no ato" col={2} mono nota="O preço do registro. Não é a avaliação.">
                  <i className="imv-pre">R$</i>
                  <input id="va-declarado" inputMode="decimal" value={valorDeclarado} onChange={(e) => setValorDeclarado(digitarReais(e.target.value))} onBlur={(e) => setValorDeclarado(completarReais(e.target.value))} placeholder="0,00" />
                </CampoDoPasso>
                {historico && (
                  <>
                    <CampoDoPasso rotulo="Transmitente" col={3}>
                      <input id="va-transmitente" value={transmitente} onChange={(e) => setTransmitente(e.target.value)} />
                    </CampoDoPasso>
                    <CampoDoPasso rotulo="Adquirente" col={3}>
                      <input id="va-adquirente" value={adquirente} onChange={(e) => setAdquirente(e.target.value)} />
                    </CampoDoPasso>
                    <CampoDoPasso rotulo="Vínculo entre as partes" col={3} nota="Só quando o documento demonstra.">
                      <input id="va-vinculo" value={vinculo} onChange={(e) => setVinculo(e.target.value)} placeholder="Irmão do investigado" />
                    </CampoDoPasso>
                    <CampoDoPasso rotulo="Proprietário atual" col={3}>
                      <input id="va-proprietario" value={proprietarioAtual} onChange={(e) => setProprietarioAtual(e.target.value)} />
                    </CampoDoPasso>
                  </>
                )}
              </div>
            )}

            {veiculo && passo === 2 && (
              <div className="imv-bloco va-bloco-topo">
                <TabelaDeAvaliacoes avaliacoes={avaliacoes} setAvaliacoes={setAvaliacoes} />
              </div>
            )}

            {veiculo && passo === 3 && (
              <div className="imv-bloco va-bloco-topo">
                {corrigindo ? (
                  <>
                    <div className="imv-tabela">
                      <div className="imv-tl imv-tl-cab imv-gr-grade">
                        <span>Natureza</span>
                        <span>Credor ou órgão</span>
                        <span>Valor</span>
                        <span>Desde</span>
                        <span>Situação</span>
                        <span />
                      </div>
                      {onusExistentes.length === 0 && <p className="imv-nada">Nenhum gravame lançado.</p>}
                      {onusExistentes.map((o) => (
                        <div key={o.id} className="imv-tl imv-gr-grade">
                          <span>{o.natureza}</span>
                          <span>{o.credor ?? '—'}</span>
                          <span className="mono">{o.valor !== null ? dinheiro(o.valor) : '—'}</span>
                          <span className="mono">{o.constituidoEm ? paraTela(o.constituidoEm) : '—'}</span>
                          <span className={`imv-mini-sit${o.situacao ? '' : ' na'}`}>{o.situacao ?? 'não apurada'}</span>
                          {ehMaster ? (
                            <button type="button" className="imv-x" aria-label="Apagar este gravame" onClick={() => setGravameAApagar(o)}>
                              <Icone nome="lixeira" tamanho={14} />
                            </button>
                          ) : (
                            <span />
                          )}
                        </div>
                      ))}
                    </div>
                    {ehMaster && (
                      <FormularioDeOnus
                        ativo={{ ...corrigindo, onus: onusExistentes }}
                        investigadoId={investigado.id}
                        aoLancar={() =>
                          void apiInvestigados
                            .ativos(investigado.id)
                            .then((r) => setOnusExistentes(r.ativos.find((x) => x.id === corrigindo.id)?.onus ?? onusExistentes))
                        }
                      />
                    )}
                    <p className="imv-nota">Gravame lançado ou apagado aqui grava na hora.</p>
                  </>
                ) : (
                  <>
                    <div className="imv-tabela">
                      <div className="imv-tl imv-tl-cab imv-gr-grade">
                        <span>Natureza</span>
                        <span>Credor ou órgão</span>
                        <span>Valor</span>
                        <span>Desde</span>
                        <span>Situação</span>
                        <span />
                      </div>
                      {gravames.length === 0 && <p className="imv-nada">Nenhum gravame.</p>}
                      {gravames.map((g, i) => {
                        const mudar = (parte: Partial<GravameNaFicha>) => setGravames((todos) => todos.map((x, k) => (k === i ? { ...x, ...parte } : x)));
                        return (
                          <div key={i} className="imv-tl imv-gr-grade">
                            <input aria-label="Natureza" list="va-naturezas" value={g.natureza} onChange={(e) => mudar({ natureza: e.target.value })} placeholder="Alienação fiduciária" />
                            <input aria-label="Credor ou órgão" value={g.credor} onChange={(e) => mudar({ credor: e.target.value })} placeholder="Banco · juízo" />
                            <input aria-label="Valor" className="mono" inputMode="decimal" value={g.valor} onChange={(e) => mudar({ valor: digitarReais(e.target.value) })} onBlur={(e) => mudar({ valor: completarReais(e.target.value) })} placeholder="—" />
                            <input aria-label="Constituído em" type="date" value={g.constituidoEm} onChange={(e) => mudar({ constituidoEm: e.target.value })} />
                            <select aria-label="Situação" value={g.situacao} onChange={(e) => mudar({ situacao: e.target.value })}>
                              <option value="">não apurada</option>
                              <option value="ATIVO">ativo</option>
                              <option value="CANCELADO">cancelado</option>
                              <option value="QUITADO SEM BAIXA">quitado sem baixa</option>
                            </select>
                            <button type="button" className="imv-x" aria-label="Tirar este gravame" onClick={() => setGravames((todos) => todos.filter((_, k) => k !== i))}>
                              <Icone nome="fechar" tamanho={14} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                    <button type="button" className="imv-mais" onClick={() => setGravames((todos) => [...todos, { natureza: '', credor: '', valor: '', constituidoEm: '', situacao: '' }])}>
                      <Icone nome="mais" tamanho={14} /> Acrescentar gravame
                    </button>
                    <datalist id="va-naturezas">
                      {NATUREZAS_DO_VEICULO.map((n) => (
                        <option key={n} value={n} />
                      ))}
                    </datalist>
                  </>
                )}
              </div>
            )}

            {/* ── ATO NOTARIAL ────────────────────────────────────────── */}
            {!veiculo && passo === 0 && (
              <>
                <span className="imv-rotulo">Espécie</span>
                <div className="imv-seg va-seg" role="radiogroup" aria-label="Espécie do ato">
                  {ESPECIE_DO_ATO.map((s) => (
                    <button
                      key={s.chave}
                      type="button"
                      role="radio"
                      aria-checked={classificacao === s.chave}
                      className={`imv-op${classificacao === s.chave ? ' on' : ''}`}
                      onClick={() => {
                        setClassificacao(s.chave);
                        /* O papel de um não vale no outro: comprador não outorga poderes. */
                        if (papelNoAto && PAPEIS.find((x) => x.chave === papelNoAto)?.deProcuracao !== (s.chave === 'procuracao')) setPapelNoAto('');
                      }}
                    >
                      <i />
                      {s.curto}
                    </button>
                  ))}
                </div>
                {especieDoAto && <p className="imv-def">{especieDoAto.define}</p>}
                <div className="imv-grade-campos va-campos">
                  <CampoDoPasso rotulo="O ato" obrigatorio col={6} nota="Nomeie o registro que detém o fato. Nunca a plataforma consultada.">
                    <input id="va-descricao" value={descricao} onChange={(e) => setDescricao(e.target.value)} placeholder={procuracao ? 'Procuração para venda de imóveis — 2º Tabelião de Notas de Campinas/SP' : PISTA.atos} autoFocus={!corrigindo} />
                  </CampoDoPasso>
                  <CampoDoPasso rotulo="Data do ato" col={2} mono>
                    <input id="va-data" type="date" value={ocorridoEm} onChange={(e) => setOcorridoEm(e.target.value)} />
                  </CampoDoPasso>
                  <CampoDoPasso rotulo="Tabelionato" col={4} nota="Pode sair no documento entregue: é o registro oficial que detém o fato.">
                    <input id="va-tabelionato" value={tabelionato} onChange={(e) => setTabelionato(e.target.value)} placeholder="2º Tabelião de Notas de Santos/SP" />
                  </CampoDoPasso>
                  <CampoDoPasso rotulo="Livro e folha" col={3} mono>
                    <input id="va-identificacao" value={identificacao} onChange={(e) => setIdentificacao(e.target.value)} placeholder="Livro 1.234, fls. 056" />
                  </CampoDoPasso>
                </div>
              </>
            )}

            {!veiculo && passo === 1 && (
              <div className="imv-grade-campos">
                <CampoDoPasso rotulo={procuracao ? 'O investigado é' : 'Qualidade do investigado no ato'} col={3}>
                  <select id="va-papel" value={papelNoAto} onChange={(e) => setPapelNoAto(e.target.value as PapelNoAtoNotarial | '')}>
                    <option value="">—</option>
                    {papeis.map((x) => (
                      <option key={x.chave} value={x.chave}>
                        {x.rotulo}
                      </option>
                    ))}
                  </select>
                </CampoDoPasso>
                {escritura ? (
                  <>
                    <span className="imv-campo c3" />
                    <CampoDoPasso rotulo="Transmitente" col={3}>
                      <input id="va-transmitente" value={transmitente} onChange={(e) => setTransmitente(e.target.value)} />
                    </CampoDoPasso>
                    <CampoDoPasso rotulo="Adquirente" col={3}>
                      <input id="va-adquirente" value={adquirente} onChange={(e) => setAdquirente(e.target.value)} />
                    </CampoDoPasso>
                    <CampoDoPasso rotulo="Vínculo entre as partes" col={3} nota="Só quando o documento demonstra.">
                      <input id="va-vinculo" value={vinculo} onChange={(e) => setVinculo(e.target.value)} placeholder="Irmão do investigado" />
                    </CampoDoPasso>
                    <CampoDoPasso rotulo="Proprietário atual" col={3}>
                      <input id="va-proprietario" value={proprietarioAtual} onChange={(e) => setProprietarioAtual(e.target.value)} />
                    </CampoDoPasso>
                  </>
                ) : (
                  <p className="imv-nota c6 va-nota-solta">Outorgante é quem dá os poderes; procurador, quem os recebe.</p>
                )}
              </div>
            )}

            {!veiculo && passo === 2 && escritura && (
              <div className="imv-grade-campos">
                <CampoDoPasso rotulo="Valor declarado no ato" col={3} mono nota="O preço que consta da escritura. Não é avaliação.">
                  <i className="imv-pre">R$</i>
                  <input id="va-declarado" inputMode="decimal" value={valorDeclarado} onChange={(e) => setValorDeclarado(digitarReais(e.target.value))} onBlur={(e) => setValorDeclarado(completarReais(e.target.value))} placeholder="0,00" />
                </CampoDoPasso>
                <CampoDoPasso rotulo="O que o ato fez com o patrimônio" col={3}>
                  <select id="va-efeito" value={efeito} onChange={(e) => setEfeito(e.target.value as EfeitoPatrimonialDoAto | '')}>
                    <option value="">—</option>
                    <OpcoesAgrupadas itens={EFEITOS_DO_ATO} atual={efeito} sufixo={sufixoDoEfeito} />
                  </select>
                </CampoDoPasso>
              </div>
            )}

            {!veiculo && passo === 2 && procuracao && (
              <div className="imv-grade-campos">
                <CampoDoPasso rotulo="Poderes patrimonialmente relevantes" col={6} nota="Resuma, não transcreva.">
                  <textarea id="va-poderes" rows={3} value={poderes} onChange={(e) => setPoderes(e.target.value)} placeholder="Comprar e vender imóveis; movimentar contas bancárias; dar quitação." />
                </CampoDoPasso>
                <CampoDoPasso rotulo="Prazo ou validade" col={3}>
                  <input id="va-prazo" value={prazo} onChange={(e) => setPrazo(e.target.value)} placeholder="Indeterminado · até 31/12/2027 · 90 dias" />
                </CampoDoPasso>
                <CampoDoPasso rotulo="Permite substabelecer?" col={3}>
                  <select id="va-substabelecer" value={substabelecer} onChange={(e) => setSubstabelecer(e.target.value as '' | 'sim' | 'nao')}>
                    <option value="">o documento não diz</option>
                    <option value="sim">sim</option>
                    <option value="nao">não</option>
                  </select>
                </CampoDoPasso>
              </div>
            )}

            {/* ── CONFERIR ────────────────────────────────────────────── */}
            {passo === ULTIMO && (
              <div className="cad-resumo">
                {veiculo ? (
                  <>
                    <ResumoDoPasso
                      titulo="O veículo"
                      aoCorrigir={() => irPara(0)}
                      linhas={[
                        ['Situação', situacao ? `${situacao.curto} — ${ENTRA_NA_CONTA[classificacao] ? 'soma no patrimônio atual' : 'não soma'}` : ''],
                        ['Espécie', ESPECIE_NO_SINGULAR[especie]],
                        ['Veículo', descricao.trim()],
                        ['Identificação', identificacao.trim()],
                      ]}
                    />
                    <ResumoDoPasso
                      titulo="Aquisição ou saída"
                      aoCorrigir={() => irPara(1)}
                      linhas={[
                        [historico ? 'Saída' : 'Aquisição', ocorridoEm ? paraTela(ocorridoEm) : ''],
                        ['Participação', percentual.trim() ? `${percentual.trim()}%` : ''],
                        ['Declarado', numero(valorDeclarado) !== null ? dinheiro(numero(valorDeclarado)!) : ''],
                        ...(historico
                          ? ([
                              ['De', transmitente.trim()],
                              ['Para', adquirente.trim()],
                              ['Vínculo', vinculo.trim()],
                              ['Hoje de', proprietarioAtual.trim()],
                            ] as Array<[string, string]>)
                          : []),
                      ]}
                    />
                    <ResumoDoPasso
                      titulo="Avaliações"
                      aoCorrigir={() => irPara(2)}
                      linhas={
                        linhasDeAvaliacao.length === 0
                          ? [['Avaliação', '']]
                          : linhasDeAvaliacao.map((a, i) => [
                              a.modalidade ? ROTULO_DA_MODALIDADE[a.modalidade] : `Avaliação ${i + 1}`,
                              `${numero(a.valor) !== null ? dinheiro(numero(a.valor)!) : '—'}${a === marcada ? ' · entra na conta' : ''}`,
                            ])
                      }
                    />
                    <ResumoDoPasso
                      titulo="Gravames"
                      aoCorrigir={() => irPara(3)}
                      linhas={
                        (corrigindo
                          ? onusExistentes.map((o) => [o.natureza, [o.credor, o.valor !== null ? dinheiro(o.valor) : null, o.situacao ?? 'não apurada'].filter(Boolean).join(' · ')])
                          : gravames
                              .filter((g) => g.natureza.trim())
                              .map((g) => [g.natureza.trim(), [g.credor.trim(), numero(g.valor) !== null ? dinheiro(numero(g.valor)!) : '', g.situacao || 'não apurada'].filter(Boolean).join(' · ')])) as Array<[string, string]>
                      }
                    />
                  </>
                ) : (
                  <>
                    <ResumoDoPasso
                      titulo="O ato"
                      aoCorrigir={() => irPara(0)}
                      linhas={[
                        ['Espécie', especieDoAto?.curto ?? ''],
                        ['Ato', descricao.trim()],
                        ['Data', ocorridoEm ? paraTela(ocorridoEm) : ''],
                        ['Tabelionato', tabelionato.trim()],
                        ['Livro e folha', identificacao.trim()],
                      ]}
                    />
                    <ResumoDoPasso
                      titulo="Quem participou"
                      aoCorrigir={() => irPara(1)}
                      linhas={[
                        [procuracao ? 'O investigado é' : 'Qualidade', papelNoAto ? ROTULO_DO_PAPEL[papelNoAto] : ''],
                        ...(escritura
                          ? ([
                              ['Transmitente', transmitente.trim()],
                              ['Adquirente', adquirente.trim()],
                              ['Vínculo', vinculo.trim()],
                              ['Proprietário atual', proprietarioAtual.trim()],
                            ] as Array<[string, string]>)
                          : []),
                      ]}
                    />
                    <div className="cad-resumo-inteiro">
                      <ResumoDoPasso
                        titulo={procuracao ? 'Os poderes' : 'O negócio'}
                        aoCorrigir={() => irPara(2)}
                        linhas={
                          procuracao
                            ? [
                                ['Poderes', poderes.trim()],
                                ['Prazo', prazo.trim()],
                                ['Substabelecer', substabelecer === '' ? '' : substabelecer === 'sim' ? 'sim' : 'não'],
                              ]
                            : [
                                ['Declarado', numero(valorDeclarado) !== null ? dinheiro(numero(valorDeclarado)!) : ''],
                                ['O que fez', efeito ? ROTULO_DO_EFEITO[efeito] : ''],
                              ]
                        }
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          {ajuda && !ajudaEmFaixa && <AjudaDoPasso titulo={ajuda.titulo}>{ajuda.corpo(classificacao)}</AjudaDoPasso>}
        </div>

        {erro && <p className="erro imv-erro">{erro}</p>}

        <footer className="imv-ficha-pe">
          <button type="button" className="imv-bt fantasma" onClick={guarda.fechar}>
            Cancelar
          </button>
          <span className="imv-esp" />
          {passo > 0 && (
            <button type="button" className="imv-bt fantasma" onClick={() => irPara(passo - 1)}>
              <Icone nome="voltar" tamanho={14} /> Voltar
            </button>
          )}
          <Progresso atual={passo} total={passos.length} />
          {passo < ULTIMO && (
            <button type="button" className={`imv-bt ${corrigindo ? 'fantasma' : 'primario'}`} onClick={avancar}>
              Avançar <Icone nome="avancar" tamanho={14} />
            </button>
          )}
          {(corrigindo || passo === ULTIMO) && (
            <button type="button" className="imv-bt primario" disabled={gravando} onClick={pedirParaGravar}>
              <Icone nome="check" tamanho={14} /> {gravando ? 'Gravando…' : corrigindo ? 'Gravar a correção' : veiculo ? 'Lançar veículo' : `Lançar ${escritura ? 'escritura' : 'procuração'}`}
            </button>
          )}
        </footer>
      </section>

      {/* Apagar sempre pergunta — a regra dele, 18/09/2026. */}
      {gravameAApagar && corrigindo && (
        <Confirmar
          titulo="Apagar este gravame?"
          descricao={`${gravameAApagar.natureza}${gravameAApagar.credor ? ` · ${gravameAApagar.credor}` : ''} sai do veículo agora, e não volta.`}
          aoFechar={() => setGravameAApagar(null)}
          aoConfirmar={() =>
            void apiInvestigados
              .apagarOnus(investigado.id, corrigindo.id, gravameAApagar.id)
              .then(() => setOnusExistentes((l) => l.filter((x) => x.id !== gravameAApagar.id)))
              .catch(avisarErro)
          }
        />
      )}
      <GuardaDoFormulario
        pendencia={guarda.pendencia}
        oQue={corrigindo ? `o lançamento de ${investigado.nome}` : `o ${nomeDaFicha} de ${investigado.nome}`}
        aoCancelar={guarda.cancelarPendencia}
        aoSalvar={() => void gravar()}
        aoDescartar={guarda.aoFecharDeVerdade}
      />
    </div>
  );
}

/** Soma no patrimônio atual? A regra é do servidor (`ENTRA_NA_CONSOLIDACAO`); aqui, só para dizer. */
const ENTRA_NA_CONTA: Partial<Record<ClassificacaoDoAtivo, boolean>> = { veiculo_atual: true, veiculo_historico: false, veiculo_baixado: false };

/** A tabela das avaliações, a mesma do imóvel: uma marcada entra na conta. */
export function TabelaDeAvaliacoes({ avaliacoes, setAvaliacoes }: { avaliacoes: LinhaDeAvaliacao[]; setAvaliacoes: React.Dispatch<React.SetStateAction<LinhaDeAvaliacao[]>> }) {
  return (
    <>
      <div className="imv-tabela">
        <div className="imv-tl imv-tl-cab imv-av-grade">
          <span title="entra na conta do patrimônio">Conta</span>
          <span>Procedência</span>
          <span>Valor</span>
          <span>Referência</span>
          <span>Data</span>
          <span />
        </div>
        {avaliacoes.length === 0 && <p className="imv-nada">Nenhuma avaliação. Sem avaliação o bem conta sem somar dinheiro — não é zero.</p>}
        {avaliacoes.map((a, i) => {
          const mudar = (parte: Partial<LinhaDeAvaliacao>) => setAvaliacoes((todas) => todas.map((x, k) => (k === i ? { ...x, ...parte } : x)));
          return (
            <div key={a.id ?? `nova-${i}`} className={`imv-tl imv-av-grade${a.conta ? ' on' : ''}`}>
              <input type="radio" aria-label="Entra na conta" checked={a.conta} onChange={() => setAvaliacoes((todas) => todas.map((x, k) => ({ ...x, conta: k === i })))} />
              <select aria-label="De onde veio a avaliação" value={a.modalidade} onChange={(e) => mudar({ modalidade: e.target.value as ModalidadeDaAvaliacao | '' })}>
                <option value="">De onde veio…</option>
                {MODALIDADES.map((m) => (
                  <option key={m.chave} value={m.chave}>
                    {m.rotulo}
                    {m.documentada ? '' : ' (derivada)'}
                  </option>
                ))}
              </select>
              <input aria-label="Valor da avaliação" className="mono" inputMode="decimal" value={a.valor} onChange={(e) => mudar({ valor: digitarReais(e.target.value) })} onBlur={(e) => mudar({ valor: completarReais(e.target.value) })} placeholder="98.000,00" />
              <input aria-label="Referência da avaliação" value={a.referencia} onChange={(e) => mudar({ referencia: e.target.value })} placeholder="Tabela pública de 09/2026, laudo…" />
              <input aria-label="Data da avaliação" type="date" value={a.avaliadaEm} onChange={(e) => mudar({ avaliadaEm: e.target.value })} />
              <button type="button" className="imv-x" aria-label="Tirar esta avaliação" onClick={() => setAvaliacoes((todas) => todas.filter((_, k) => k !== i))}>
                <Icone nome="fechar" tamanho={14} />
              </button>
            </div>
          );
        })}
      </div>
      <button type="button" className="imv-mais" onClick={() => setAvaliacoes((todas) => [...todas, { modalidade: '', valor: '', referencia: '', avaliadaEm: '', conta: todas.every((x) => !x.conta) }])}>
        <Icone nome="mais" tamanho={14} /> Acrescentar avaliação
      </button>
      <p className="imv-nota">
        Do bem integral. Judicial, laudo e valor declarado avaliam <b>este</b> bem; as demais são <b>derivadas</b> — valem, e o relatório dirá que são. <b>A apurar</b> não é zero.
      </p>
    </>
  );
}

/** A coluna da direita, passo a passo. Texto de tela: sem fonte, sem ferramenta. */
const AJUDA_DO_VEICULO: Array<{ titulo: string; corpo: (c: ClassificacaoDoAtivo) => ReactNode }> = [
  {
    titulo: 'Qual situação?',
    corpo: () => (
      <>
        <p>
          <b>Atual</b> está no nome do investigado e soma. <b>Histórico</b> saiu do nome dele; <b>baixado</b> é baixa, sucateamento ou perda total — os dois não somam.
        </p>
        <p>Espécie e situação são eixos diferentes: a espécie só diz que bem é.</p>
      </>
    ),
  },
  {
    titulo: 'Preço não é avaliação',
    corpo: (c) => (
      <>
        <p>
          O <b>valor declarado</b> é o preço que consta do registro. A <b>avaliação</b> é quanto o bem vale — e é ela que soma. É a distância entre os dois que interessa.
        </p>
        {c === 'veiculo_historico' && <p>Na saída, diga para quem foi: o vínculo entre as partes é o que o relatório precisa ler.</p>}
      </>
    ),
  },
  {
    titulo: 'Qual avaliação conta?',
    corpo: () => (
      <p>
        Marque <b>uma</b> como a que entra na conta. Sem nenhuma, o veículo conta sem somar dinheiro — não é zero.
      </p>
    ),
  },
  {
    titulo: 'Gravame',
    corpo: () => (
      <>
        <p>
          O valor do gravame é o do ato registrado, <b>não o saldo devedor</b> — e nunca se subtrai do patrimônio.
        </p>
        <p>Situação em branco é "não apurada": só gravame ativo entra na exposição.</p>
      </>
    ),
  },
];

const AJUDA_DO_ATO: Array<{ titulo: string; corpo: (c: ClassificacaoDoAtivo) => ReactNode }> = [
  {
    titulo: 'Escritura ou procuração',
    corpo: () => (
      <>
        <p>
          O <b>tabelionato</b> pode sair no documento entregue: é o registro oficial que detém o fato, como a Receita e a junta.
        </p>
        <p>Nenhum dos dois soma no patrimônio atual.</p>
      </>
    ),
  },
  {
    titulo: 'Quem participou',
    corpo: (c) =>
      c === 'procuracao' ? (
        <p>
          Na procuração, o investigado é <b>outorgante</b> (dá os poderes) ou <b>procurador</b> (recebe).
        </p>
      ) : (
        <p>A qualidade é o papel do investigado neste ato — vendedor, doador, comprador. O vínculo só se escreve quando o documento o demonstra.</p>
      ),
  },
  {
    titulo: 'O que o ato fez',
    corpo: (c) =>
      c === 'procuracao' ? (
        <>
          <p>
            Poder para vender <b>não é venda feita</b>: a procuração mostra o que se pode fazer, não o que se fez.
          </p>
          <p>Poder para substabelecer não significa que houve substabelecimento.</p>
        </>
      ) : (
        <p>
          Não é a classificação: aquela diz o que o lançamento <b>é</b>, esta diz o que o ato <b>fez</b>. "Sem efeito identificado" é resposta legítima.
        </p>
      ),
  },
];
